/**
 * Smart Add system: add mapped AHRI partners that are not already in the cart.
 * Also drives the outdoor and air-handler "Get Full Package" wizards.
 * Never invents AHRI numbers, BTU, or AFUE — options come from rendered JSON.
 */
(function () {
  const LABELS = window.ahriPackageStrings || {};

  const BTU_FROM_MODEL = {
    '030': '30000',
    '045': '45000',
    '070': '70000',
    '090': '90000',
    '110': '110000',
    '135': '135000',
    '140': '140000',
  };
  const CAPACITY_TO_BTU = {
    '018': '18000',
    '024': '24000',
    '030': '30000',
    '036': '36000',
    '042': '42000',
    '048': '48000',
    '060': '60000',
    18: '18000',
    24: '24000',
    30: '30000',
    36: '36000',
    42: '42000',
    48: '48000',
    60: '60000',
  };
  const KIT_TYPE_LABELS = {
    circuit_breaker: 'Circuit breaker',
    terminal_block: 'Terminal block',
  };
  const OUTDOOR_TYPE_HEAT_PUMP = 'heat_pump';
  const OUTDOOR_TYPE_AC = 'air_conditioner';
  const OUTDOOR_TYPE_ORDER = [OUTDOOR_TYPE_HEAT_PUMP, OUTDOOR_TYPE_AC];
  const OUTDOOR_TYPE_LABELS = {
    heat_pump: LABELS.outdoorHeatPump || 'Heat pump',
    air_conditioner: LABELS.outdoorAirConditioner || 'Air conditioner',
  };
  const SERIES_AFUE = {
    ML180: '80',
    EL180: '80',
    ML280: '80',
    EL280: '80',
    ML193: '93',
    EL193: '93',
    ML195: '95',
    EL195: '95',
    ML196: '96',
    EL196: '96',
    ML296: '96',
    EL296: '96',
    ML297: '97',
    EL297: '97',
    SLP98: '98',
    SLP99: '99',
  };
  const CABINET_WIDTH = { A: '14.5', B: '17.5', C: '21', D: '24.5' };
  const AFUE_BANDS = [
    { key: '80', label: '80%', match: (n) => n === 80 },
    { key: '90-95', label: '90–95%', match: (n) => n >= 90 && n <= 95 },
    { key: '96+', label: '96%+', match: (n) => n >= 96 },
  ];

  function partnerHay(part) {
    if (!part) return '';
    return [
      part.title,
      part.sku,
      part.handle,
      part.model,
      part.model_ahri,
      part.name,
      part.afue,
      part.btu,
      part.width,
      part.kw,
      part.amps,
      part.kitType,
      part.series,
      part.tons,
      part.tonnage,
      part.capacity,
      part.cooling_btuh,
      part.cooling_btu,
      part.cooling_capacity,
      part.type,
      part.product_type,
    ]
      .filter(Boolean)
      .join(' ');
  }

  /**
   * Typo-tolerant match against existing option text only (never invents SKUs).
   * Exact/contains first; then one letter edit (substitution, extra/missing
   * letter, or adjacent swap) against model tokens so Cvk45 matches CBK45.
   * Digits stay exact so CBK45 and CBK47 / ML196 and ML193 stay distinct.
   */
  function matchesSearch(hay, query) {
    const needle = String(query || '').toLowerCase().trim();
    if (!needle) return true;
    const hayText = String(hay || '').toLowerCase();
    if (hayText.includes(needle)) return true;
    const compactNeedle = needle.replace(/[^a-z0-9]+/g, '');
    if (!compactNeedle) return true;
    if (hayText.replace(/[^a-z0-9]+/g, '').includes(compactNeedle)) return true;
    return fuzzyModelMatch(hayText, compactNeedle);
  }

  function fuzzyModelMatch(hay, needle) {
    if (needle.length < 4) return false;
    const tokens = String(hay || '').toLowerCase().match(/[a-z0-9]{4,}/g) || [];
    return tokens.some((token) => tokenNearNeedle(token, needle));
  }

  function lettersOf(value) {
    return String(value || '').replace(/[^a-z]/g, '');
  }

  function digitsOf(value) {
    return String(value || '').replace(/[^0-9]/g, '');
  }

  function tokenNearNeedle(token, needle) {
    if (token.includes(needle)) return true;
    const needleLetters = lettersOf(needle);
    const needleDigits = digitsOf(needle);
    if (!needleLetters) return false;
    const windows = [];
    if (Math.abs(token.length - needle.length) <= 1) windows.push(token);
    if (token.length > needle.length) {
      for (let i = 0; i <= token.length - needle.length; i += 1) {
        windows.push(token.slice(i, i + needle.length));
      }
    }
    return windows.some((window) => {
      if (digitsOf(window) !== needleDigits) return false;
      return withinOneEdit(lettersOf(window), needleLetters);
    });
  }

  function withinOneEdit(a, b) {
    if (a === b) return true;
    const la = a.length;
    const lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    if (la === lb) {
      let diffs = 0;
      let first = -1;
      for (let i = 0; i < la; i += 1) {
        if (a[i] === b[i]) continue;
        diffs += 1;
        if (diffs === 1) {
          first = i;
        } else if (diffs === 2 && first === i - 1 && a[first] === b[i] && a[i] === b[first]) {
          return a.slice(i + 1) === b.slice(i + 1);
        } else {
          return false;
        }
      }
      return diffs <= 1;
    }
    const longer = la > lb ? a : b;
    const shorter = la > lb ? b : a;
    let skipped = false;
    let i = 0;
    let j = 0;
    while (i < longer.length && j < shorter.length) {
      if (longer[i] === shorter[j]) {
        i += 1;
        j += 1;
      } else if (!skipped) {
        skipped = true;
        i += 1;
      } else {
        return false;
      }
    }
    return true;
  }

  function parseSeries(text) {
    const hay = String(text || '').toUpperCase();
    const furnace = hay.match(/(SLP\d{2}|EL\d{3}|ML\d{3})/);
    if (furnace) return furnace[1];
    const indoor = hay.match(
      /(?:^|[^A-Z0-9])((?:ECBA|ECB|CBK|CBA|CBX)\d{2}|CBC\d|MB[A-Z]{2,3}|(?:CK|CX|CH|CR)\d{2})/
    );
    if (indoor) return indoor[1];
    // Outdoor families: EL22KCV, ML13KC1, SL25KPV, XP21. Furnace EL280/ML196 already returned.
    const outdoor = hay.match(/(?:^|[^A-Z0-9])((?:EL|ML|SL)\d{2}[A-Z]{2,}[0-9]?|XP\d{2})/);
    return outdoor ? outdoor[1] : '';
  }

  function parseAfue(text) {
    const hay = String(text || '');
    const marked = hay.match(/(\d{2}(?:\.\d+)?)\s*%?\s*AFUE/i);
    if (marked) return String(Math.round(Number(marked[1])));
    const series = parseSeries(hay);
    return SERIES_AFUE[series] || '';
  }

  function parseIndoorCapacity(text) {
    const hay = String(text || '').toUpperCase();
    const three = hay.match(
      /(?:^|[^A-Z0-9])(?:CBA|CBX|CBK|CBC|MB)[A-Z0-9]*[- _]*?(018|024|030|036|042|048|060)(?:[-_ *]|$|\d{3})/
    );
    if (three) return CAPACITY_TO_BTU[three[1]] || '';
    const two = hay.match(
      /(?:^|[^A-Z0-9])(?:CBA|CBX|CBK|CBC|MB)[A-Z0-9]*[- _](18|24|30|36|42|48|60)(?:[-_ *]|$)/
    );
    if (two) return CAPACITY_TO_BTU[two[1]] || '';
    const uh = hay.match(/(?:UH|MV|ET|VT)[A-Z]*[- _]*?(018|024|030|036|042|048|060)/);
    return uh ? CAPACITY_TO_BTU[uh[1]] || '' : '';
  }

  /**
   * Canonical cooling/heating BTU for filter chips. Accepts tons, 018–060
   * capacity codes, and rated BTUH in a nominal bucket. Does not invent sizes
   * that are not already in the partner / matchup text.
   */
  function normalizeBtu(value) {
    if (value == null || value === '') return '';
    const raw = String(value).replace(/,/g, '').trim();
    if (!raw) return '';
    const digits = raw.replace(/btuh?/i, '').trim();
    if (CAPACITY_TO_BTU[digits]) return CAPACITY_TO_BTU[digits];
    const padded = /^\d{1,2}$/.test(digits) ? digits.padStart(3, '0') : '';
    if (padded && CAPACITY_TO_BTU[padded]) return CAPACITY_TO_BTU[padded];
    const n = Number(digits);
    if (n >= 1 && n <= 5) return String(Math.round(n * 12000));
    if (
      /^(18000|24000|30000|36000|40000|42000|45000|48000|60000|66000|70000|80000|90000|100000|110000|115000|120000|135000|140000)$/.test(
        digits
      )
    ) {
      return digits;
    }
    if (n >= 54000 && n <= 63000) return '60000';
    if (n >= 48000 && n < 54000) return '48000';
    if (n >= 42000 && n < 48000) return '42000';
    if (n >= 36000 && n < 42000) return '36000';
    if (n >= 30000 && n < 36000) return '30000';
    if (n >= 24000 && n < 30000) return '24000';
    if (n >= 18000 && n < 24000) return '18000';
    return parseBtu(raw);
  }

  function parseBtu(text) {
    const hay = String(text || '');
    const labeled = hay.replace(/,/g, '').match(/(\d{4,6})\s*BTU/i);
    if (labeled) return labeled[1];
    const compact = hay
      .replace(/,/g, '')
      .match(
        /\b(18000|24000|30000|36000|40000|42000|45000|48000|60000|66000|70000|80000|90000|100000|110000|115000|120000|135000|140000)\b/
      );
    if (compact) return compact[1];
    const model = hay.toUpperCase().match(/(?:UH|DF|HZ)(030|045|070|090|110|135|140)/);
    if (model) return BTU_FROM_MODEL[model[1]] || '';
    const tons = hay.match(/(\d(?:\.\d)?)\s*[- ]?tons?\b/i);
    if (tons) {
      const n = Number(tons[1]);
      if (n >= 1 && n <= 5) return String(Math.round(n * 12000));
    }
    const indoor = parseIndoorCapacity(hay);
    if (indoor) return indoor;
    const coil = hay
      .toUpperCase()
      .match(/(?:^|[^A-Z0-9])(?:CK|CX|CH|CR)\d{2}[A-Z]*[- _](18|24|30|36|42|48|60)[ABCD]?(?:[-_ ]|$)/);
    if (coil) return CAPACITY_TO_BTU[coil[1]] || '';
    const outdoor = hay
      .toUpperCase()
      .match(/[-_](018|024|030|036|042|048|060|18|24|30|36|42|48|60)(?:[-_ ]|[A-Z]|$)/);
    if (outdoor) return CAPACITY_TO_BTU[outdoor[1]] || '';
    return '';
  }

  function parseWidth(text) {
    const hay = String(text || '');
    const exact = hay.match(/\b(14\.5|17\.5|18\.5|21\.5|22\.5|24\.5|26\.5)\b/);
    if (exact) return exact[1];
    if (/(?:^|[^\d])21\s*(?:"|in|inch|inches)\b/i.test(hay) || /21"/.test(hay)) return '21';
    const cabinet = hay.toUpperCase().match(/(?:UH|DF|HZ)[A-Z0-9]*?(\d{2})([ABCD])(?:\b|$)/);
    if (cabinet) return CABINET_WIDTH[cabinet[2]] || '';
    const coilCab = hay.toUpperCase().match(/(?:CK|CX|CH|CR)\d{2}[A-Z]*[-_]\d{2}([ABCD])(?:[-_]|$)/);
    if (coilCab) return CABINET_WIDTH[coilCab[1]] || '';
    const tail = hay.toUpperCase().match(/(\d{2})([ABCD])$/);
    if (tail) return CABINET_WIDTH[tail[2]] || '';
    return '';
  }

  function parseKw(text) {
    const hay = String(text || '');
    const labeled = hay.match(/(\d+(?:\.\d+)?)\s*kW/i);
    if (labeled) return labeled[1];
    const model = hay.toUpperCase().match(/ECBA?\d{2}[-_](\d{1,2}(?:\.\d+)?)/);
    return model ? model[1] : '';
  }

  function parseAmps(text) {
    const hay = String(text || '');
    const labeled = hay.match(/(\d{2,3})\s*(?:A|amp|amps)\b/i);
    return labeled ? labeled[1] : '';
  }

  function parseKitType(text) {
    const hay = String(text || '');
    if (
      /circuit\s*breaker/i.test(hay) ||
      /\d(?:\.\d+)?CB(?:\b|$)/i.test(hay) ||
      /[-_]CB(?:\b|$)/i.test(hay) ||
      /\bCB\b/.test(hay)
    ) {
      return 'circuit_breaker';
    }
    if (/terminal\s*block/i.test(hay) || /[-_]TB(?:\b|$)/i.test(hay) || /\bTB\b/.test(hay)) {
      return 'terminal_block';
    }
    return '';
  }

  function enrichPartner(part) {
    if (!part) return part;
    const hay = partnerHay(part);
    const next = Object.assign({}, part);
    next.series = parseSeries(next.series) || parseSeries(hay) || next.series || '';
    if (!next.afue) next.afue = parseAfue(hay);
    const modelHay = [part.title, part.sku, part.handle, part.model, part.model_ahri, part.name, part.series]
      .filter(Boolean)
      .join(' ');
    next.btu =
      normalizeBtu(next.btu) ||
      parseBtu(modelHay) ||
      normalizeBtu(part.cooling_btuh || part.cooling_btu || part.cooling_capacity || part.tons || part.tonnage) ||
      parseBtu(hay);
    if (!next.width) next.width = parseWidth(hay);
    if (!next.kw) next.kw = parseKw(hay);
    if (!next.amps) next.amps = parseAmps(hay);
    if (!next.kitType) next.kitType = parseKitType(hay);
    return next;
  }

  function afueBand(afue) {
    const n = Number(afue);
    if (!n) return '';
    const band = AFUE_BANDS.find((row) => row.match(n));
    return band ? band.key : '';
  }

  function widthOk(furnace, coil) {
    if (!furnace || !coil) return false;
    const fw = String(furnace.width || '').trim();
    const cw = String(coil.width || '').trim();
    if (!fw || !cw) return false;
    return fw === cw;
  }

  function partnerKey(part) {
    if (!part) return '';
    return String(part.handle || part.sku || part.title || part.model || '').trim();
  }

  function furnaceKey(furnace) {
    if (!furnace) return '';
    const row = enrichPartner(furnace);
    return [partnerKey(row), row.btu || '', row.afue || '', row.width || ''].join('|');
  }

  // Furnace cards only record the furnace. Cart add is addChosen() on Add System.
  // Outdoor PDPs continue to a width-matched coil. Coil PDPs continue to outdoor.
  function applyFurnacePick(choice, furnace, nextStep) {
    const row = enrichPartner(furnace);
    const nextChoice = Object.assign({}, choice || {}, {
      furnaceKey: furnaceKey(row),
      btu: row.btu || '',
      afue: row.afue || '',
      coilKey: '',
      combo: null,
    });
    if (nextStep === 'outdoor') nextChoice.outdoorKey = '';
    return {
      choice: nextChoice,
      step: nextStep || 'coil',
    };
  }

  function comboAhri(combo) {
    if (!combo) return '';
    return String(combo.ahri || combo.ahri_number || '').trim();
  }

  // Coil-anchored matchups identify this indoor by product_id or sku only.
  // Handle / model are display fields — matching on them would invent a lock.
  function indoorMatchesPdp(part, pdp) {
    if (!part || !pdp) return false;
    const productId = String(pdp.productId || pdp.id || '').trim();
    const sku = String(pdp.sku || '').trim();
    const partPid = String(part.product_id || part.shopify_product_id || part.shopify_id || '').trim();
    const partSku = String(part.sku || part.variant_sku || '').trim();
    if (productId && partPid && productId === partPid) return true;
    if (sku && partSku && sku === partSku) return true;
    return false;
  }

  function coilPartnerFromCombo(combo) {
    if (!combo) return null;
    if (combo.coil && partnerKey(combo.coil)) return combo.coil;
    if (combo.indoor_type && combo.indoor_type !== 'coil') return null;
    if (combo.indoor && (partnerKey(combo.indoor) || combo.indoor.model || combo.indoor.sku || combo.indoor.product_id)) {
      if (looksLikeAirHandler(combo.indoor) && combo.indoor_type !== 'coil') return null;
      return combo.indoor;
    }
    return null;
  }

  // Fail-closed coil package rows: AHRI# + this indoor + furnace + outdoor.
  // Rejects air_handler system_path / non-coil indoor_type / outdoor-anchored rows
  // when those Catalog fields are present. Width uses the same furnace↔coil rule.
  function coilPackageCombos(combos, pdp) {
    const identity = pdp || {};
    if (!String(identity.productId || identity.sku || '').trim()) return [];
    return (combos || [])
      .map((combo) => {
        if (!combo || !comboAhri(combo)) return null;
        if (combo.system_path && combo.system_path !== 'furnace_coil') return null;
        if (combo.anchor_role && combo.anchor_role !== 'indoor') return null;
        if (combo.indoor_type && combo.indoor_type !== 'coil') return null;
        const coil = coilPartnerFromCombo(combo);
        if (!coil) return null;
        if (!indoorMatchesPdp(coil, identity) && !indoorMatchesPdp(combo.indoor, identity)) return null;
        if (!combo.furnace || !partnerKey(combo.furnace) || looksLikeAirHandler(combo.furnace)) return null;
        if (!combo.outdoor || !partnerKey(combo.outdoor)) return null;
        const furnace = enrichPartner(combo.furnace);
        const coilRow = enrichPartner(coil);
        const outdoor = enrichPartner(combo.outdoor);
        if (furnace.width && coilRow.width && furnace.width !== coilRow.width) return null;
        return Object.assign({}, combo, { ahri: comboAhri(combo), furnace, coil: coilRow, outdoor });
      })
      .filter(Boolean);
  }

  function comboForCoilPackage(combos, choice, pdp) {
    const matches = coilPackageCombos(combos, pdp).filter((combo) => {
      if (choice && choice.furnaceKey && furnaceKey(combo.furnace) !== choice.furnaceKey) return false;
      if (choice && choice.outdoorKey && partnerKey(combo.outdoor) !== choice.outdoorKey) return false;
      return true;
    });
    return matches[0] || null;
  }

  // Launch button sits in the PDP product form. Detach the dialog / unbind
  // controls so a furnace tap cannot submit Add to Cart.
  const UNBOUND_FORM_ID = 'ahri-package-unbound';

  function unbindFromHostForm(el) {
    if (!el || !el.setAttribute) return el;
    el.setAttribute('form', UNBOUND_FORM_ID);
    if (el.getAttribute && el.getAttribute('type') !== 'submit') {
      el.setAttribute('type', 'button');
    }
    return el;
  }

  function ensureUnboundForm(doc) {
    const root = doc || (typeof document !== 'undefined' ? document : null);
    if (!root || !root.createElement) return null;
    const existing = root.getElementById ? root.getElementById(UNBOUND_FORM_ID) : null;
    if (existing) return existing;
    const host = root.body || root.documentElement;
    if (!host || !host.appendChild) return null;
    const form = root.createElement('form');
    form.id = UNBOUND_FORM_ID;
    form.hidden = true;
    if (form.setAttribute) {
      form.setAttribute('aria-hidden', 'true');
      form.setAttribute('data-ahri-package-unbound', '');
    }
    if (form.addEventListener) {
      form.addEventListener('submit', suppressWizardFormEvent);
    }
    host.appendChild(form);
    return form;
  }

  function isolateWizardDialog(dialog, doc) {
    const root = doc || (typeof document !== 'undefined' ? document : null);
    if (!dialog || !root || !root.body) return null;
    const home = dialog.parentNode;
    if (home && home !== root.body) {
      root.body.appendChild(dialog);
      return home;
    }
    return null;
  }

  function restoreWizardDialog(dialog, home) {
    if (!dialog || !home) return;
    if (dialog.parentNode !== home) home.appendChild(dialog);
  }

  function isWizardDialogOpen(dialog) {
    if (!dialog) return false;
    if (dialog.open === true) return true;
    return Boolean(dialog.hasAttribute && dialog.hasAttribute('open'));
  }

  function applyWizardDialogClosed(dialog) {
    if (!dialog) return dialog;
    try {
      if (dialog.close && dialog.open) dialog.close();
    } catch (e) {
      /* already closed or not a native dialog */
    }
    if (dialog.removeAttribute) dialog.removeAttribute('open');
    if (dialog.open === true) {
      try {
        dialog.open = false;
      } catch (e) {
        /* read-only in some hosts */
      }
    }
    if (dialog.setAttribute) {
      dialog.setAttribute('inert', '');
      dialog.setAttribute('aria-hidden', 'true');
    }
    return dialog;
  }

  function applyWizardDialogOpen(dialog) {
    if (!dialog) return dialog;
    if (dialog.removeAttribute) {
      dialog.removeAttribute('inert');
      dialog.removeAttribute('aria-hidden');
    }
    if (dialog.showModal) {
      try {
        if (!dialog.open) dialog.showModal();
      } catch (e) {
        if (dialog.setAttribute) dialog.setAttribute('open', '');
      }
    } else if (dialog.setAttribute) {
      dialog.setAttribute('open', '');
    }
    return dialog;
  }

  function isWizardBackdropClick(event, dialog) {
    if (!event || !dialog) return false;
    if (event.target === dialog) return true;
    const panel = dialog.querySelector ? dialog.querySelector('.ahri-package-dialog__panel') : null;
    if (!panel) return false;
    if (panel.contains && event.target && panel.contains(event.target)) return false;
    if (typeof event.composedPath === 'function') {
      const path = event.composedPath();
      if (path && path.indexOf(panel) >= 0) return false;
    }
    return false;
  }

  function bindWizardCloseControl(el, closer) {
    if (!el || typeof el.addEventListener !== 'function') return el;
    unbindFromHostForm(el);
    const onClose = (event) => {
      suppressWizardFormEvent(event);
      if (event && typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      if (typeof closer === 'function') closer();
    };
    el.addEventListener('click', onClose, true);
    el.addEventListener('pointerup', onClose, true);
    return el;
  }

  function suppressWizardFormEvent(event) {
    if (!event) return false;
    if (typeof event.preventDefault === 'function') event.preventDefault();
    if (typeof event.stopPropagation === 'function') event.stopPropagation();
    return false;
  }

  function isWizardFormSubmitKey(event) {
    if (!event) return false;
    const key = event.key || event.code || '';
    return key === 'Enter' || key === 'NumpadEnter';
  }

  function comboHasCoil(combo) {
    return Boolean(combo && combo.coil && partnerKey(combo.coil));
  }

  function listableFurnaceCombos(combos) {
    return (combos || [])
      .map((combo) => {
        if (!combo || !combo.furnace || !partnerKey(combo.furnace) || !comboHasCoil(combo)) return null;
        const furnace = enrichPartner(combo.furnace);
        const coil = enrichPartner(combo.coil);
        if (furnace.width && coil.width && furnace.width !== coil.width) return null;
        return Object.assign({}, combo, { furnace, coil });
      })
      .filter(Boolean);
  }

  const AIR_HANDLER_MODEL = /(?:^|[^A-Z0-9])(CBA|CBX|CBK|CBC|MB)[A-Z0-9]/i;

  function looksLikeAirHandler(part) {
    if (!part) return false;
    if (part.role === 'air_handler') return true;
    const hay = partnerHay(part);
    if (/air[\s-]?handler/i.test(hay)) return true;
    return AIR_HANDLER_MODEL.test(hay);
  }

  function furnaceMissing(combo) {
    if (!combo || !combo.furnace) return true;
    if (looksLikeAirHandler(combo.furnace) && !/(?:^|[^A-Z0-9])(SLP\d{2}|EL\d{3}|ML\d{3})/i.test(partnerHay(combo.furnace))) {
      return true;
    }
    return !partnerKey(combo.furnace);
  }

  function indoorCandidate(combo) {
    if (!combo) return null;
    if (combo.indoor && (partnerKey(combo.indoor) || combo.indoor.model)) {
      const indoor = combo.indoor;
      if (indoor.title || indoor.handle || indoor.sku) return indoor;
      return Object.assign({}, indoor, { title: indoor.model || partnerKey(indoor) });
    }
    const indoorModel = combo.indoor_model || combo.indoor_title || combo.indoor_sku || combo.indoor_handle;
    if (indoorModel) return { title: indoorModel, model: indoorModel };
    return null;
  }

  function comboCoolingBtu(combo) {
    if (!combo) return '';
    return combo.cooling_btuh || combo.cooling_btu || combo.cooling_capacity || '';
  }

  function withComboCooling(part, combo) {
    if (!part || !comboCoolingBtu(combo)) return part;
    if (part.btu || part.cooling_btuh || part.cooling_btu || part.cooling_capacity) return part;
    // Stamp rated system BTUH as a fallback field only. enrichPartner prefers
    // a size parsed from the indoor model so a 060 handler on a 042 outdoor
    // stays 5-ton, not the outdoor's 3.5-ton rating.
    return Object.assign({}, part, { cooling_btuh: comboCoolingBtu(combo) });
  }

  function airHandlerFromCombo(combo) {
    if (!combo) return null;
    if (combo.air_handler && partnerKey(combo.air_handler)) {
      return withComboCooling(combo.air_handler, combo);
    }
    if (!furnaceMissing(combo)) return null;
    const indoor = indoorCandidate(combo);
    if (indoor && looksLikeAirHandler(indoor)) return withComboCooling(indoor, combo);
    if (combo.coil && looksLikeAirHandler(combo.coil)) return withComboCooling(combo.coil, combo);
    if (combo.furnace && looksLikeAirHandler(combo.furnace)) return withComboCooling(combo.furnace, combo);
    return null;
  }

  function airHandlerCombos(combos) {
    return (combos || [])
      .map((combo) => {
        const airHandler = airHandlerFromCombo(combo);
        if (!airHandler || !partnerKey(airHandler)) return null;
        if (combo.air_handler === airHandler) return combo;
        return Object.assign({}, combo, { air_handler: airHandler });
      })
      .filter(Boolean);
  }

  function airHandlerKey(part) {
    if (!part) return '';
    const row = enrichPartner(part);
    const identity = partnerKey(row);
    if (!identity) return '';
    return [identity, row.series || '', row.btu || ''].join('|');
  }

  function uniqueAirHandlerPartners(combos) {
    const values = [];
    const seen = new Set();
    airHandlerCombos(combos).forEach((combo) => {
      const ah = enrichPartner(combo.air_handler);
      const key = airHandlerKey(ah);
      if (!key || seen.has(key)) return;
      seen.add(key);
      values.push(ah);
    });
    return values.sort((a, b) => {
      const series = String(a.series || '').localeCompare(String(b.series || ''));
      if (series) return series;
      const btu = Number(a.btu || 0) - Number(b.btu || 0);
      if (btu) return btu;
      return String(a.title || partnerKey(a)).localeCompare(String(b.title || partnerKey(b)));
    });
  }

  function uniqueFurnaces(combos, btu, afue) {
    const values = [];
    const seen = new Set();
    listableFurnaceCombos(combos).forEach((combo) => {
      const furnace = combo.furnace;
      if (btu && furnace.btu && furnace.btu !== btu) return;
      if (afue && furnace.afue && furnace.afue !== afue) return;
      const key = furnaceKey(furnace);
      if (!key || seen.has(key)) return;
      seen.add(key);
      values.push(furnace);
    });
    return values.sort((a, b) => {
      const series = String(a.series || '').localeCompare(String(b.series || ''));
      if (series) return series;
      const afue = Number(a.afue || 0) - Number(b.afue || 0);
      if (afue) return afue;
      return String(a.title || partnerKey(a)).localeCompare(String(b.title || partnerKey(b)));
    });
  }

  function furnaceFilterOptions(furnaces) {
    const afue = [];
    const btu = [];
    const width = [];
    const series = [];
    (furnaces || []).forEach((furnace) => {
      const band = afueBand(furnace.afue);
      if (band && !afue.includes(band)) afue.push(band);
      if (furnace.btu && !btu.includes(furnace.btu)) btu.push(furnace.btu);
      if (furnace.width && !width.includes(furnace.width)) width.push(furnace.width);
      if (furnace.series && !series.includes(furnace.series)) series.push(furnace.series);
    });
    return {
      afue: AFUE_BANDS.filter((band) => afue.includes(band.key)),
      btu: btu.sort((a, b) => Number(a) - Number(b)),
      width: width.slice().sort((a, b) => Number(a) - Number(b)),
      series: series.slice().sort(),
    };
  }

  function filterFurnaces(furnaces, filters) {
    const search = String((filters && filters.search) || '');
    const afue = (filters && filters.afue) || [];
    const btu = (filters && filters.btu) || [];
    const width = (filters && filters.width) || [];
    const series = (filters && filters.series) || [];
    return (furnaces || []).filter((furnace) => {
      if (search.trim() && !matchesSearch(partnerHay(furnace), search)) return false;
      if (afue.length && !afue.includes(afueBand(furnace.afue))) return false;
      if (btu.length && !btu.includes(furnace.btu)) return false;
      if (width.length && !width.includes(furnace.width)) return false;
      if (series.length && !series.includes(furnace.series)) return false;
      return true;
    });
  }

  const FILTER_TIERS = ['afue', 'btu', 'width', 'series'];

  function furnaceTierValue(furnace, tier) {
    if (tier === 'afue') return afueBand(furnace && furnace.afue);
    return (furnace && furnace[tier]) || '';
  }

  function earlierFurnaceFilters(filters, tier) {
    const index = FILTER_TIERS.indexOf(tier);
    const next = { search: (filters && filters.search) || '' };
    FILTER_TIERS.forEach((key, keyIndex) => {
      next[key] = keyIndex < index ? ((filters && filters[key]) || []).slice() : [];
    });
    return next;
  }

  function pruneFurnaceFilters(furnaces, filters) {
    const next = {
      search: (filters && filters.search) || '',
      afue: ((filters && filters.afue) || []).slice(),
      btu: ((filters && filters.btu) || []).slice(),
      width: ((filters && filters.width) || []).slice(),
      series: ((filters && filters.series) || []).slice(),
    };
    FILTER_TIERS.forEach((tier) => {
      const remaining = filterFurnaces(furnaces, earlierFurnaceFilters(next, tier));
      const enabled = new Set(remaining.map((row) => furnaceTierValue(row, tier)).filter(Boolean));
      next[tier] = next[tier].filter((value) => enabled.has(value));
    });
    return next;
  }

  function cascadeFurnaceFilters(furnaces, filters) {
    const pruned = pruneFurnaceFilters(furnaces, filters);
    const all = furnaceFilterOptions(furnaces);
    const labels = {
      afue: (all.afue || []).map((row) => ({ key: row.key, label: row.label })),
      btu: (all.btu || []).map((value) => ({ key: value, label: formatBtu(value) })),
      width: (all.width || []).map((value) => ({ key: value, label: `${value}"` })),
      series: (all.series || []).map((value) => ({ key: value, label: value })),
    };
    const groups = FILTER_TIERS.map((tier) => {
      const remaining = filterFurnaces(furnaces, earlierFurnaceFilters(pruned, tier));
      const enabled = new Set(remaining.map((row) => furnaceTierValue(row, tier)).filter(Boolean));
      return {
        key: tier,
        items: (labels[tier] || []).map((item) => ({
          key: item.key,
          label: item.label,
          enabled: enabled.has(item.key),
        })),
      };
    });
    return { filters: pruned, groups };
  }

  const AIR_HANDLER_FILTER_TIERS = ['btu', 'width', 'series'];
  const OUTDOOR_FILTER_TIERS = ['btu', 'series'];
  const AH_OUTDOOR_FILTER_TIERS = ['outdoorType'].concat(OUTDOOR_FILTER_TIERS);
  const COIL_FILTER_TIERS = ['width', 'btu', 'series'];
  const HEAT_KIT_FILTER_TIERS = ['kw', 'amps', 'kitType', 'series'];

  function partnerTierValue(part, tier) {
    if (!part) return '';
    if (tier === 'afue') return afueBand(part.afue);
    if (tier === 'btu') return normalizeBtu(part.btu) || parseBtu(partnerHay(part));
    if (tier === 'series') return parseSeries(part.series) || parseSeries(partnerHay(part)) || part.series || '';
    if (tier === 'outdoorType') return outdoorUnitType(part);
    return part[tier] || '';
  }

  function partnerTierEquals(tier, value, selected) {
    if (tier === 'btu') {
      const left = normalizeBtu(value);
      const right = normalizeBtu(selected);
      return Boolean(left) && left === right;
    }
    if (tier === 'series') {
      const left = parseSeries(value) || String(value || '');
      const right = parseSeries(selected) || String(selected || '');
      return Boolean(left) && left === right;
    }
    return String(value || '') === String(selected || '');
  }

  function earlierPartnerFilters(filters, tiers, tier) {
    const index = tiers.indexOf(tier);
    const next = { search: (filters && filters.search) || '' };
    (tiers || []).forEach((key, keyIndex) => {
      next[key] = keyIndex < index ? ((filters && filters[key]) || []).slice() : [];
    });
    return next;
  }

  function otherPartnerFilters(filters, tiers, tier) {
    const next = { search: (filters && filters.search) || '' };
    (tiers || []).forEach((key) => {
      next[key] = key === tier ? [] : ((filters && filters[key]) || []).slice();
    });
    return next;
  }

  function filterPartners(items, filters, tiers) {
    const search = String((filters && filters.search) || '');
    const keys = tiers || [];
    return (items || []).filter((item) => {
      if (search.trim() && !matchesSearch(partnerHay(item), search)) return false;
      return keys.every((tier) => {
        const selected = (filters && filters[tier]) || [];
        if (!selected.length) return true;
        const value = partnerTierValue(item, tier);
        return Boolean(value) && selected.some((want) => partnerTierEquals(tier, value, want));
      });
    });
  }

  function partnerFilterOptions(items, tiers) {
    const options = {};
    (tiers || []).forEach((tier) => {
      const values = [];
      (items || []).forEach((item) => {
        const value = partnerTierValue(item, tier);
        if (value && !values.includes(value)) values.push(value);
      });
      if (tier === 'outdoorType') {
        values.sort((a, b) => OUTDOOR_TYPE_ORDER.indexOf(a) - OUTDOOR_TYPE_ORDER.indexOf(b));
      } else if (tier === 'series' || tier === 'kitType') values.sort();
      else values.sort((a, b) => Number(a) - Number(b));
      options[tier] = values;
    });
    return options;
  }

  function prunePartnerFilters(items, filters, tiers) {
    const next = { search: (filters && filters.search) || '' };
    (tiers || []).forEach((tier) => {
      next[tier] = ((filters && filters[tier]) || []).slice();
    });
    (tiers || []).forEach((tier) => {
      const remaining = filterPartners(items, earlierPartnerFilters(next, tiers, tier), tiers);
      next[tier] = next[tier].filter((value) =>
        remaining.some((row) => partnerTierEquals(tier, partnerTierValue(row, tier), value))
      );
    });
    return next;
  }

  function partnerFilterLabel(tier, value) {
    if (tier === 'btu') return formatCapacity(value);
    if (tier === 'width') return `${value}"`;
    if (tier === 'kw') return `${value} kW`;
    if (tier === 'amps') return `${value}A`;
    if (tier === 'kitType') return KIT_TYPE_LABELS[value] || value;
    if (tier === 'outdoorType') return OUTDOOR_TYPE_LABELS[value] || value;
    if (tier === 'afue') {
      const band = AFUE_BANDS.find((row) => row.key === value);
      return band ? band.label : value;
    }
    return String(value || '');
  }

  function cascadePartnerFilters(items, filters, tiers) {
    const keys = tiers || [];
    let pruned = prunePartnerFilters(items, filters, keys);
    if (!searchQuery(pruned) && (items || []).length && !filterPartners(items, pruned, keys).length) {
      pruned = { search: '' };
      keys.forEach((tier) => {
        pruned[tier] = [];
      });
    }
    const all = partnerFilterOptions(items, keys);
    const groups = keys.map((tier) => {
      const remaining = filterPartners(items, otherPartnerFilters(pruned, keys, tier), keys);
      const enabled = new Set(remaining.map((row) => partnerTierValue(row, tier)).filter(Boolean));
      return {
        key: tier,
        items: (all[tier] || []).map((value) => ({
          key: value,
          label: partnerFilterLabel(tier, value),
          enabled: enabled.has(value),
        })),
      };
    });
    return { filters: pruned, groups };
  }

  function formatCapacity(btu) {
    const n = Number(btu);
    if (!n) return String(btu || '');
    const tons = n / 12000;
    const neat = Math.round(tons * 2) / 2;
    if (Math.abs(tons - neat) < 0.05 && neat >= 1 && neat <= 5) {
      return `${n.toLocaleString()} BTU · ${neat} ton`;
    }
    return `${n.toLocaleString()} BTU`;
  }

  function airHandlerMeta(part) {
    if (!part) return '';
    return [part.btu ? formatCapacity(part.btu) : '', part.width ? `${part.width}"` : ''].filter(Boolean).join(' · ');
  }

  function outdoorMeta(part) {
    if (!part) return '';
    return part.btu ? formatCapacity(part.btu) : '';
  }

  function uniqueOutdoorPartners(combos) {
    const values = [];
    const seen = new Set();
    (combos || []).forEach((combo) => {
      if (!combo || !combo.outdoor) return;
      const outdoor = enrichPartner(combo.outdoor);
      const key = partnerKey(outdoor);
      if (!key || seen.has(key)) return;
      seen.add(key);
      values.push(outdoor);
    });
    return values.sort((a, b) => {
      const series = String(a.series || '').localeCompare(String(b.series || ''));
      if (series) return series;
      const btu = Number(a.btu || 0) - Number(b.btu || 0);
      if (btu) return btu;
      return String(a.title || partnerKey(a)).localeCompare(String(b.title || partnerKey(b)));
    });
  }

  function listUniqueOutdoors(combos) {
    return uniqueOutdoorPartners(airHandlerCombos(combos));
  }

  function coilMeta(part, combo) {
    if (!part) return ahriCertLabel(combo);
    return [part.width ? `${part.width}"` : '', part.btu ? formatCapacity(part.btu) : '', ahriCertLabel(combo)]
      .filter(Boolean)
      .join(' · ');
  }

  function heatKitMeta(part, options) {
    if (!part) return '';
    const opts = options || {};
    return [
      opts.recommended ? opts.recommendedLabel || 'Recommended' : '',
      part.kw ? `${part.kw} kW` : '',
      part.amps ? `${part.amps}A` : '',
      part.kitType ? KIT_TYPE_LABELS[part.kitType] || part.kitType : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }

  function heatKitsForMatchup(combos, filterKey, by) {
    const values = [];
    const seen = new Set();
    const pool = airHandlerCombos(combos).filter((combo) => {
      if (by === 'air_handler') return partnerKey(combo.air_handler) === filterKey;
      return partnerKey(combo.outdoor) === filterKey;
    });
    pool.forEach((combo) => {
      const kit = enrichPartner(combo.heat_kit);
      const key = partnerKey(kit);
      if (!kit || !key || seen.has(key)) return;
      seen.add(key);
      values.push(kit);
    });
    return values;
  }

  // Adam's locked rule: kit heat BTU = kW × 3412; recommend when that is
  // 70–110% of system BTU from the outdoor/AH size-code map (060 → 60000).
  const HEAT_KIT_KW_TO_BTU = 3412;
  const HEAT_KIT_RECOMMEND_MIN_RATIO = 0.7;
  const HEAT_KIT_RECOMMEND_MAX_RATIO = 1.1;

  function outdoorTypeHay(part) {
    if (!part) return '';
    const tags = Array.isArray(part.tags) ? part.tags.filter(Boolean).join(' ') : part.tags;
    return [partnerHay(part), part.type, part.product_type, tags, part.kind]
      .filter(Boolean)
      .join(' ');
  }

  function looksLikeHeatPump(part) {
    if (!part) return false;
    const hay = outdoorTypeHay(part);
    if (/heat[\s-]?pump/i.test(hay)) return true;
    if (/air[\s-]?condition/i.test(hay)) return false;
    const model = hay.toUpperCase();
    if (/(?:^|[^A-Z0-9])(?:EL|ML|SL)\d{2}(?:KCV|KPV|KP)/.test(model)) return true;
    if (/(?:^|[^A-Z0-9])XP\d{2}/.test(model)) return true;
    return false;
  }

  // Cooling-only outdoor families already used here (ML13KC1, EL16KC1). KC not
  // followed by V/P so EL22KCV / SL25KPV stay heat-pump-only. Unknown → ''.
  function looksLikeAirConditioner(part) {
    if (!part || looksLikeHeatPump(part)) return false;
    const hay = outdoorTypeHay(part);
    if (/air[\s-]?condition/i.test(hay)) return true;
    const model = hay.toUpperCase();
    return /(?:^|[^A-Z0-9])(?:EL|ML|SL)\d{2}KC(?![VP])/.test(model);
  }

  function outdoorUnitType(part) {
    if (looksLikeHeatPump(part)) return OUTDOOR_TYPE_HEAT_PUMP;
    if (looksLikeAirConditioner(part)) return OUTDOOR_TYPE_AC;
    return '';
  }

  function shouldOfferHeatKitStep(outdoor, path) {
    if (path && path !== 'air_handler') return false;
    return looksLikeHeatPump(outdoor);
  }

  function stepAfterAirHandlerPartner(_kits, outdoor, path) {
    return shouldOfferHeatKitStep(outdoor, path) ? 'heat_kit' : 'summary';
  }

  function heatKitStepEmpty(kits) {
    return !(kits && kits.length);
  }

  function systemBtuFromSizeCode(outdoor, airHandler) {
    const outdoorBtu = Number((enrichPartner(outdoor) || {}).btu) || 0;
    if (outdoorBtu) return outdoorBtu;
    return Number((enrichPartner(airHandler) || {}).btu) || 0;
  }

  function heatKitOutputBtu(kit) {
    const row = enrichPartner(kit);
    const kw = Number(row && row.kw);
    if (!kw) return 0;
    return kw * HEAT_KIT_KW_TO_BTU;
  }

  function heatKitIsRecommended(kit, systemBtu) {
    const kitBtu = heatKitOutputBtu(kit);
    const system = Number(systemBtu) || 0;
    if (!kitBtu || !system) return false;
    const ratio = kitBtu / system;
    return ratio >= HEAT_KIT_RECOMMEND_MIN_RATIO && ratio <= HEAT_KIT_RECOMMEND_MAX_RATIO;
  }

  function airHandlerSeries(part) {
    const row = enrichPartner(part);
    return String((row && row.series) || '').toUpperCase();
  }

  // CBA25 → ECBA25, then ECB25. Sort/label only — does not invent kits.
  function heatKitSeriesKeys(ahSeries) {
    const series = String(ahSeries || '').toUpperCase();
    const keys = [];
    const push = (value) => {
      if (value && keys.indexOf(value) < 0) keys.push(value);
    };
    if (!series) return keys;
    if (/^CB[AKX]\d{2}$/.test(series) || /^CBC\d$/.test(series)) push(`E${series}`);
    const digits = (series.match(/(\d+)/) || [])[1];
    if (digits) push(`ECB${digits}`);
    push(series);
    return keys;
  }

  function heatKitSeriesRank(kit, airHandler) {
    const keys = heatKitSeriesKeys(airHandlerSeries(airHandler));
    if (!keys.length) return 99;
    const row = enrichPartner(kit);
    const kitSeries = String((row && row.series) || '').toUpperCase();
    const hay = partnerHay(row).toUpperCase();
    const idx = keys.findIndex((key) => kitSeries === key || hay.indexOf(key) >= 0);
    return idx < 0 ? 99 : idx;
  }

  function heatKitSeriesMatched(kit, airHandler) {
    return heatKitSeriesRank(kit, airHandler) < 99;
  }

  function sortHeatKits(kits, airHandler, outdoor) {
    const systemBtu = systemBtuFromSizeCode(outdoor, airHandler);
    return (kits || []).slice().sort((a, b) => {
      const recA = heatKitIsRecommended(a, systemBtu) ? 0 : 1;
      const recB = heatKitIsRecommended(b, systemBtu) ? 0 : 1;
      if (recA !== recB) return recA - recB;
      const seriesA = heatKitSeriesRank(a, airHandler);
      const seriesB = heatKitSeriesRank(b, airHandler);
      if (seriesA !== seriesB) return seriesA - seriesB;
      const kw = Number(a.kw || 0) - Number(b.kw || 0);
      if (kw) return kw;
      return String(a.title || partnerKey(a)).localeCompare(String(b.title || partnerKey(b)));
    });
  }

  // Sources: nested combo kits, then catalog (metafield → collection role).
  function collectHeatKits(combos, filterKey, by, catalogKits, airHandler, outdoor) {
    const values = [];
    const seen = new Set();
    const add = (kit) => {
      const row = enrichPartner(kit);
      const key = partnerKey(row);
      if (!row || !key || seen.has(key)) return;
      seen.add(key);
      values.push(row);
    };
    heatKitsForMatchup(combos, filterKey, by).forEach(add);
    (catalogKits || []).forEach(add);
    return sortHeatKits(values, airHandler, outdoor);
  }

  function heatKitSkipChoice(labels) {
    return {
      key: '',
      title: (labels && labels.skipHeatKit) || 'No heat kit',
      kit: null,
      cartable: true,
    };
  }

  function heatKitStepChoices(kits, labels) {
    return [heatKitSkipChoice(labels)].concat(kits || []);
  }

  function airHandlerComboMatches(combos, choice) {
    const airHandlerKey = choice && choice.airHandlerKey;
    const outdoorKey = choice && choice.outdoorKey;
    return airHandlerCombos(combos).filter((combo) => {
      if (airHandlerKey) return partnerKey(combo.air_handler) === airHandlerKey;
      return partnerKey(combo.outdoor) === outdoorKey;
    });
  }

  function comboWithoutHeatKitRequirement(matches) {
    if (!matches || !matches.length) return null;
    const skipCombo = matches.find((combo) => !combo.heat_kit);
    if (skipCombo) return skipCombo;
    return matches[0];
  }

  function heatKitInList(kits, key) {
    if (!key) return false;
    return (kits || []).some((kit) => partnerKey(kit) === key);
  }

  // Selected certified kit → that combo. Skip / catalog accessory → outdoor+AH
  // combo without requiring nested heat_kit. Unknown kit key fails closed.
  function comboForAirHandler(combos, choice, catalogKits) {
    const matches = airHandlerComboMatches(combos, choice);
    if (!matches.length) return null;
    const heatKitKey = choice && choice.heatKitKey;
    if (heatKitKey === '' || heatKitKey == null) {
      return comboWithoutHeatKitRequirement(matches);
    }
    const certified = matches.find((combo) => partnerKey(combo.heat_kit) === heatKitKey);
    if (certified) return certified;
    if (heatKitInList(catalogKits, heatKitKey)) {
      return comboWithoutHeatKitRequirement(matches);
    }
    return null;
  }

  function chosenHeatKitPart(choice, combo, catalogKits) {
    const key = choice && choice.heatKitKey;
    if (!key) return null;
    if (combo && partnerKey(combo.heat_kit) === key) return combo.heat_kit;
    return (catalogKits || []).find((kit) => partnerKey(kit) === key) || null;
  }

  function airHandlerComboBlockedMessage(combo, heatKitKey, labels) {
    const L = labels || {};
    if (combo) return '';
    if (heatKitKey) {
      return (
        L.heatKitMiss ||
        'No certified combo for this heat kit. Go back and choose another option.'
      );
    }
    return L.empty || 'No catalog products to add for this matchup.';
  }

  function visibleFilterTiers(groups) {
    return (groups || []).filter((group) => group.items && group.items.length);
  }

  function firstFilterTier(groups) {
    const visible = visibleFilterTiers(groups);
    return visible[0] ? visible[0].key : '';
  }

  function nextFilterTier(groups, currentKey) {
    const visible = visibleFilterTiers(groups);
    const index = visible.findIndex((group) => group.key === currentKey);
    if (index < 0) return firstFilterTier(groups);
    return visible[index + 1] ? visible[index + 1].key : currentKey;
  }

  function filterGroupSummary(label, selectedLabels) {
    const picked = (selectedLabels || []).filter(Boolean).join(', ');
    return picked ? `${label} · ${picked}` : String(label || '');
  }

  function searchQuery(filters) {
    return String((filters && filters.search) || '').trim();
  }

  function applySearchTokens(template, tokens) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
      tokens[key] == null ? '' : String(tokens[key])
    );
  }

  function pickerSearchCount(countText, searchCountText, n, query) {
    const q = searchQuery({ search: query });
    if (!q) return String(countText || '');
    return applySearchTokens(searchCountText || '{{ n }} matching “{{ q }}”', { n, q });
  }

  function pickerSearchEmpty(emptyText, searchEmptyText, query) {
    const q = searchQuery({ search: query });
    if (!q) return String(emptyText || '');
    return applySearchTokens(
      searchEmptyText || 'No options match “{{ q }}”. Clear search to see the list again.',
      { q }
    );
  }

  function resolveOpenFilterTier(groups, currentOpen) {
    if (currentOpen === '') return '';
    const visible = visibleFilterTiers(groups);
    if (currentOpen && visible.some((group) => group.key === currentOpen)) {
      return currentOpen;
    }
    return firstFilterTier(groups);
  }

  function openTierAfterSelect(groups, currentKey) {
    const next = nextFilterTier(groups, currentKey);
    return next && next !== currentKey ? next : '';
  }

  function shopifyHandleize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Storefront /products/{handle}.js keys from explicit catalog mapping only.
   * Accepts Shopify handle fields and a handleized SKU. Never invents a handle
   * from AHRI model or title — those are display strings, not mappings.
   */
  function catalogLookupHandles(part) {
    if (!part) return [];
    const handles = [];
    const handle = String(part.handle || part.product_handle || part.shopify_handle || '').trim();
    if (handle) handles.push(handle);
    const sku = String(part.sku || part.variant_sku || '').trim();
    const skuHandle = shopifyHandleize(sku);
    if (skuHandle && !handles.includes(skuHandle)) handles.push(skuHandle);
    return handles;
  }

  function hasExplicitVariantId(part) {
    if (!part) return false;
    const id = part.id;
    return !(id == null || id === '' || id === false || id === 0);
  }

  function isIndoorPart(part) {
    return Boolean(part && (part.role === 'coil' || part.role === 'air_handler'));
  }

  function letterAllows(part) {
    return Boolean(part && (part.letter_ok === true || part.letter_ok === 'true'));
  }

  // One refuse check. Liquid owns the letter rule; this only reads letter_ok.
  function coilRefused(part) {
    if (!part || part.role !== 'coil') return false;
    if (part.coilUnmatched) return true;
    return !letterAllows(part);
  }

  function hostRefusesCoil(root) {
    if (!root || typeof root.querySelector !== 'function') return false;
    return Boolean(root.querySelector('[data-coil-letter-ok="false"]'));
  }

  function hostMissingPartnerId(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return false;
    return Array.from(root.querySelectorAll('[data-partner-role]')).some(
      (el) => !el.hidden && !el.hasAttribute('data-variant-id')
    );
  }

  function markCoilUnstocked(part) {
    return Object.assign({}, part, {
      id: null,
      available: false,
      coilUnmatched: true,
      letter_ok: false,
    });
  }

  function mappedHandle(part) {
    return String((part && (part.handle || part.product_handle || part.shopify_handle)) || '').trim();
  }

  function mappedSku(part) {
    return String((part && (part.sku || part.variant_sku)) || '').trim();
  }

  function variantIdsToAdd(parts) {
    return (parts || []).filter((part) => isCartable(part)).map((part) => part.id);
  }

  /**
   * Cartability is decided here — same rule addChosen uses.
   * A partner can ATC only when it has a real published Shopify variant id
   * and is not marked unavailable. Handleized AHRI model names are not mappings.
   * Unmapped / unpublished / missing-variant rows are not cartable.
   */
  function isCartable(part) {
    if (!part || coilRefused(part)) return false;
    const id = part.id;
    if (id == null || id === '' || id === false || id === 0) return false;
    return part.available !== false;
  }

  function cartabilityReason(part) {
    if (isCartable(part)) return '';
    if (part && part.id) return 'unavailable';
    return 'unmapped';
  }

  function requiredCartParts(combo, path) {
    if (!combo) return [];
    if (path === 'air_handler') {
      return [combo.outdoor, airHandlerFromCombo(combo) || combo.air_handler].filter(Boolean);
    }
    return [combo.outdoor, combo.furnace, combo.coil].filter(Boolean);
  }

  function comboCartable(combo, path) {
    const parts = requiredCartParts(combo, path);
    return parts.length > 0 && parts.every(isCartable);
  }

  function furnaceChoiceState(furnace, combos) {
    if (!isCartable(furnace)) {
      return { cartable: false, reason: cartabilityReason(furnace) };
    }
    const key = furnaceKey(furnace);
    const related = listableFurnaceCombos(combos).filter((combo) => furnaceKey(combo.furnace) === key);
    if (related.some((combo) => comboCartable(combo, 'furnace'))) {
      return { cartable: true, reason: '' };
    }
    // Unstocked coils are still opened so the coil step can show the call message.
    if (related.length && related.every((combo) => coilRefused(combo.coil))) {
      return { cartable: true, reason: '' };
    }
    return { cartable: false, reason: 'no_combo' };
  }

  function partnerChoiceState(part, extraReason) {
    if (!isCartable(part)) {
      return { cartable: false, reason: cartabilityReason(part) };
    }
    if (extraReason) return { cartable: false, reason: extraReason };
    return { cartable: true, reason: '' };
  }

  function shouldOfferChoice(item) {
    if (!item || item.cartable !== false) return true;
    return Boolean(item.reason && item.reason !== 'unmapped');
  }

  function offerablePartnerItems(items) {
    return (items || []).filter((item) => shouldOfferChoice(partnerChoiceState(item)));
  }

  // Cascade / prune / filter one partner list. Unmapped rows stay out of the
  // pool so a series/BTU chip cannot select into a hidden empty list.
  function applyPartnerFilters(items, filters, tiers) {
    const pool = offerablePartnerItems(items);
    const cascaded = cascadePartnerFilters(pool, filters, tiers);
    return {
      pool,
      filters: cascaded.filters,
      groups: cascaded.groups,
      filtered: filterPartners(pool, cascaded.filters, tiers),
    };
  }

  window.AhriPackageUtils = {
    widthOk,
    partnerKey,
    furnaceKey,
    applyFurnacePick,
    comboAhri,
    indoorMatchesPdp,
    coilPartnerFromCombo,
    coilPackageCombos,
    comboForCoilPackage,
    uniqueOutdoorPartners,
    isolateWizardDialog,
    restoreWizardDialog,
    ensureUnboundForm,
    isWizardDialogOpen,
    applyWizardDialogClosed,
    applyWizardDialogOpen,
    isWizardBackdropClick,
    bindWizardCloseControl,
    suppressWizardFormEvent,
    isWizardFormSubmitKey,
    unbindFromHostForm,
    UNBOUND_FORM_ID,
    parseSeries,
    parseAfue,
    parseBtu,
    parseIndoorCapacity,
    normalizeBtu,
    parseWidth,
    enrichPartner,
    afueBand,
    listableFurnaceCombos,
    looksLikeAirHandler,
    airHandlerFromCombo,
    airHandlerCombos,
    airHandlerKey,
    uniqueAirHandlerPartners,
    uniqueFurnaces,
    furnaceFilterOptions,
    matchesSearch,
    filterFurnaces,
    pruneFurnaceFilters,
    cascadeFurnaceFilters,
    filterPartners,
    partnerTierValue,
    partnerFilterOptions,
    earlierPartnerFilters,
    otherPartnerFilters,
    prunePartnerFilters,
    cascadePartnerFilters,
    offerablePartnerItems,
    applyPartnerFilters,
    parseKw,
    parseAmps,
    parseKitType,
    formatCapacity,
    airHandlerMeta,
    outdoorMeta,
    uniqueOutdoors: listUniqueOutdoors,
    coilMeta,
    heatKitMeta,
    heatKitsForMatchup,
    looksLikeHeatPump,
    looksLikeAirConditioner,
    outdoorUnitType,
    shouldOfferHeatKitStep,
    stepAfterAirHandlerPartner,
    heatKitStepEmpty,
    systemBtuFromSizeCode,
    heatKitOutputBtu,
    heatKitIsRecommended,
    heatKitSeriesKeys,
    heatKitSeriesRank,
    heatKitSeriesMatched,
    sortHeatKits,
    collectHeatKits,
    heatKitSkipChoice,
    heatKitStepChoices,
    comboForAirHandler,
    chosenHeatKitPart,
    HEAT_KIT_KW_TO_BTU,
    HEAT_KIT_RECOMMEND_MIN_RATIO,
    HEAT_KIT_RECOMMEND_MAX_RATIO,
    refreshPdpHeatKitRecommendations,
    airHandlerComboBlockedMessage,
    firstFilterTier,
    nextFilterTier,
    filterGroupSummary,
    searchQuery,
    applySearchTokens,
    pickerSearchCount,
    pickerSearchEmpty,
    resolveOpenFilterTier,
    openTierAfterSelect,
    shopifyHandleize,
    catalogLookupHandles,
    coilRefused,
    hostRefusesCoil,
    hostMissingPartnerId,
    variantIdsToAdd,
    isCartable,
    cartabilityReason,
    requiredCartParts,
    comboCartable,
    furnaceChoiceState,
    partnerChoiceState,
    shouldOfferChoice,
    FILTER_TIERS,
    AIR_HANDLER_FILTER_TIERS,
    OUTDOOR_FILTER_TIERS,
    AH_OUTDOOR_FILTER_TIERS,
    COIL_FILTER_TIERS,
    HEAT_KIT_FILTER_TIERS,
    AFUE_BANDS,
    KIT_TYPE_LABELS,
  };

  function cartRoot() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function usableSectionHtml(html) {
    return typeof html === 'string' && html.trim() !== '';
  }

  function pickUsableSections(source) {
    const out = {};
    if (!source || typeof source !== 'object') return out;
    Object.keys(source).forEach((id) => {
      if (usableSectionHtml(source[id])) out[id] = source[id];
    });
    return out;
  }

  function isSectionRefreshError(error) {
    const message = String((error && error.message) || error || '');
    return /DOMParser|parseFromString/i.test(message);
  }

  // Shopify's Section Rendering API splits on raw commas. Encoding the whole
  // list (`cart-drawer%2Ccart-icon-bubble`) is one unknown id → empty/null HTML.
  function sectionsQuery(ids) {
    return `sections=${(ids || []).map((id) => encodeURIComponent(id)).join(',')}`;
  }

  function drawerLineCount(html) {
    const source = String(html || '');
    const byId = (source.match(/id=["']CartDrawer-Item-\d+["']/g) || []).length;
    if (byId) return byId;
    return (source.match(/class=["'][^"']*\bcart-item\b[^"']*["']/g) || []).length;
  }

  function liveDrawerLineCount(drawer) {
    if (!drawer || !drawer.querySelectorAll) return 0;
    const items = drawer.querySelectorAll('.cart-item');
    return items && items.length ? items.length : 0;
  }

  function wait(ms) {
    if (typeof setTimeout === 'function' && ms > 0) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }
    return Promise.resolve();
  }

  function sectionRequestUrls(ids) {
    const query = sectionsQuery(ids);
    const urls = [];
    const seen = {};
    function add(base) {
      if (!base || seen[base]) return;
      seen[base] = true;
      urls.push(`${base}?${query}`);
    }
    add(typeof routes !== 'undefined' && routes.cart_url);
    add(typeof window !== 'undefined' && window.location && window.location.pathname);
    add('/cart');
    return urls;
  }

  async function fetchSections(ids) {
    const unique = Array.from(new Set((ids || []).filter(Boolean)));
    if (!unique.length) return {};
    const urls = sectionRequestUrls(unique);
    let best = {};
    let bestLines = -1;
    for (let i = 0; i < urls.length; i += 1) {
      try {
        const res = await fetch(urls[i], { headers: { Accept: 'application/json' } });
        if (!res.ok) continue;
        const usable = pickUsableSections(await res.json());
        const lines = drawerLineCount(usable['cart-drawer']);
        if (lines > bestLines) {
          best = usable;
          bestLines = lines;
        }
        if (bestLines > 0) break;
      } catch (error) {
        continue;
      }
    }
    return best;
  }

  function dropStaleSections(sections, expectedCount) {
    const out = pickUsableSections(sections);
    const expected = Number(expectedCount) || 0;
    if (!expected) return out;
    const lines = drawerLineCount(out['cart-drawer']);
    const drawerMissingOrShort = !out['cart-drawer'] || lines < expected;
    if (drawerMissingOrShort) {
      // Fail closed: empty/partial/missing drawer HTML must not paint a $0
      // cart, and must not update the bag badge while the drawer is empty.
      if (out['cart-drawer'] && lines < expected) {
        try {
          if (typeof console !== 'undefined' && console.error) {
            console.error('AHRI cart refresh: dropping stale cart-drawer section HTML', {
              expected,
              drawerLines: lines,
            });
          }
        } catch (error) {}
      }
      delete out['cart-drawer'];
      delete out['cart-icon-bubble'];
    }
    return out;
  }

  async function fetchDrawerHtml() {
    const bases = [];
    const seen = {};
    function add(base) {
      if (!base || seen[base]) return;
      seen[base] = true;
      bases.push(String(base));
    }
    add(typeof routes !== 'undefined' && routes.cart_url);
    add('/cart');
    for (let i = 0; i < bases.length; i += 1) {
      try {
        const res = await fetch(`${bases[i]}?section_id=cart-drawer`, {
          headers: { Accept: 'text/html' },
        });
        if (!res.ok || typeof res.text !== 'function') continue;
        const text = await res.text();
        const trimmed = String(text || '').trim();
        if (!trimmed || trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') continue;
        if (drawerLineCount(trimmed) < 1) continue;
        return trimmed;
      } catch (error) {
        continue;
      }
    }
    return '';
  }

  const REFRESH_ATTEMPTS = 6;

  async function fetchCommittedSections(ids, expectedCount) {
    const expected = Number(expectedCount) || 0;
    const attempts = expected > 0 ? REFRESH_ATTEMPTS : 1;
    let sections = {};
    for (let i = 0; i < attempts; i += 1) {
      try {
        sections = await fetchSections(ids);
      } catch (error) {
        sections = {};
      }
      sections = pickUsableSections(sections);
      if (!expected || drawerLineCount(sections['cart-drawer']) >= expected) {
        return sections;
      }
      if (i < attempts - 1) await wait(80 * (i + 1));
    }
    return dropStaleSections(sections, expected);
  }

  function applyBadgeCount(count) {
    if (typeof count !== 'number' || count < 1) return;
    const bubble = typeof document !== 'undefined' ? document.getElementById('cart-icon-bubble') : null;
    if (!bubble || !bubble.querySelector) return;
    const hidden = bubble.querySelector('.cart-count-bubble [aria-hidden="true"]');
    if (hidden) hidden.textContent = String(count);
  }

  function applySectionPayload(cartEl, drawer, sections, expected) {
    const itemsEl = drawer && drawer.querySelector ? drawer.querySelector('cart-drawer-items') : null;
    if (itemsEl && typeof itemsEl.applyRefreshedSections === 'function') {
      itemsEl.applyRefreshedSections(sections, { expectedItemCount: expected });
      return;
    }
    if (typeof cartEl.renderContents === 'function' && usableSectionHtml(sections['cart-drawer'])) {
      cartEl.renderContents({ sections });
    }
  }

  async function refreshCartUI(options) {
    const opts = options || {};
    const shouldOpen = opts.open !== false;
    try {
      const drawer = document.querySelector('cart-drawer');
      const notification = document.querySelector('cart-notification');
      const cartEl = drawer || notification;
      if (!cartEl) return;

      let cart = null;
      let expected = Number(opts.expectedItemCount) || 0;
      if (!expected) {
        cart = await fetchCart().catch(() => null);
        expected = cart && cart.items ? cart.items.length : 0;
      }

      const wanted =
        typeof cartEl.getSectionsToRender === 'function'
          ? cartEl.getSectionsToRender().map((section) => section.id).filter(Boolean)
          : ['cart-drawer', 'cart-icon-bubble'];

      // GET live sections after the cart commits. /cart/add.js HTML can omit
      // lines already in the cart, and an immediate GET can still be empty.
      let sections = {};
      try {
        sections = await fetchCommittedSections(wanted, expected);
      } catch (e) {
        sections = {};
      }
      sections = dropStaleSections(sections, expected);

      try {
        applySectionPayload(cartEl, drawer, sections, expected);
      } catch (error) {
        // Add already succeeded; a missing cart section must not crash the page.
      }

      let painted = liveDrawerLineCount(drawer);

      if (expected && painted < expected) {
        try {
          const html = await fetchDrawerHtml();
          if (drawerLineCount(html) >= expected) {
            sections = dropStaleSections({ 'cart-drawer': html }, expected);
            applySectionPayload(cartEl, drawer, sections, expected);
            painted = liveDrawerLineCount(drawer);
          }
        } catch (error) {}
      }

      if (expected && painted < expected) {
        if (!cart) cart = await fetchCart().catch(() => null);
        if (cart && cart.items && cart.items.length && drawer && typeof drawer.paintFromCart === 'function') {
          try {
            if (typeof console !== 'undefined' && console.error) {
              console.error('AHRI cart refresh: painting drawer from /cart.js after section HTML miss', {
                expected,
                painted,
                lines: cart.items.length,
              });
            }
          } catch (error) {}
          drawer.paintFromCart(cart);
          painted = liveDrawerLineCount(drawer);
        }
      }

      if (drawer && typeof drawer.syncFilledState === 'function') {
        drawer.syncFilledState(painted > 0);
      } else if (painted > 0 && drawer) {
        drawer.classList.remove('is-empty');
        const inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        const items = drawer.querySelector('cart-drawer-items');
        if (items) items.classList.remove('is-empty');
      }

      // Never show badge N while the drawer still has the empty-state copy.
      if (painted > 0 && sections['cart-icon-bubble'] == null) {
        applyBadgeCount(Number(cart && cart.item_count) || painted);
      }

      if (shouldOpen && drawer && typeof drawer.open === 'function') {
        drawer.open(document.querySelector('#cart-icon-bubble'));
      }
    } catch (error) {
      try {
        if (opts.open === false) return;
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open(document.querySelector('#cart-icon-bubble'));
        }
      } catch (openError) {}
    }
  }

  async function fetchCart() {
    const res = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  function idsInCart(cart) {
    const ids = new Set();
    (cart.items || []).forEach((item) => {
      if (item.variant_id) ids.add(String(item.variant_id));
      if (item.id) ids.add(String(item.id));
    });
    return ids;
  }

  function productJsUrl(handle) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}products/${encodeURIComponent(handle)}.js`;
  }

  function phoneHref(value) {
    const href = String(value || '');
    return /^tel:\+\d{10,15}$/.test(href) ? href : '';
  }

  function appendNoStockedCoil(el) {
    if (!el) return;
    el.replaceChildren();
    el.append(document.createTextNode((LABELS.noStockedCoil || 'No stocked coil for this match.') + ' '));
    const call = LABELS.noStockedCoilCall || 'Call us.';
    const href = phoneHref(LABELS.phoneTel);
    if (href) {
      const link = document.createElement('a');
      link.className = 'ahri-package-dialog__call';
      link.href = href;
      link.textContent = call;
      el.append(link);
    } else {
      el.append(document.createTextNode(call));
    }
  }

  async function resolvePartVariant(part) {
    if (!part) return part;
    // Indoor parts are not chosen from model text. A coil Liquid marked
    // letter_ok false is dropped, even with an explicit id. When letter_ok
    // is true and there is no id yet, load only the mapped handle and keep
    // the variant whose SKU equals the mapped SKU.
    if (isIndoorPart(part)) return resolveMappedIndoor(part);
    if (hasExplicitVariantId(part)) return part;
    const tries = catalogLookupHandles(part);
    if (!tries.length) {
      return Object.assign({}, part, { available: false });
    }
    for (const candidate of tries) {
      try {
        const res = await fetch(productJsUrl(candidate), { headers: { Accept: 'application/json' } });
        if (!res.ok) continue;
        const product = await res.json();
        const variants = product.variants || [];
        const variant = variants.find((row) => row.available) || variants[0];
        if (!variant) continue;
        const next = Object.assign({}, part, {
          id: variant.id,
          handle: product.handle || part.handle,
          sku: variant.sku || part.sku,
          title: part.title || product.title,
          available: Boolean(variant.available),
        });
        if (!next.type && product.product_type) next.type = product.product_type;
        if (!next.product_type && product.product_type) next.product_type = product.product_type;
        if (!next.tags && product.tags) next.tags = product.tags;
        return next;
      } catch (error) {
        continue;
      }
    }
    return Object.assign({}, part, { available: false });
  }

  async function resolveMappedIndoor(part) {
    if (part.role === 'coil' && !letterAllows(part)) return markCoilUnstocked(part);
    if (part.letter_ok === false || part.letter_ok === 'false') {
      return Object.assign({}, part, { id: null, available: false });
    }
    if (hasExplicitVariantId(part)) return part;
    const handle = mappedHandle(part);
    const sku = mappedSku(part);
    if (!handle || !sku) {
      return part.role === 'coil' ? markCoilUnstocked(part) : Object.assign({}, part, { available: false });
    }
    try {
      const res = await fetch(productJsUrl(handle), { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('miss');
      const product = await res.json();
      const variant = (product.variants || []).find((row) => String((row && row.sku) || '').trim() === sku);
      if (!variant) {
        return part.role === 'coil' ? markCoilUnstocked(part) : Object.assign({}, part, { available: false });
      }
      const next = Object.assign({}, part, {
        id: variant.id,
        handle: product.handle || handle,
        sku: variant.sku || sku,
        title: part.title || product.title,
        available: Boolean(variant.available),
      });
      if (!next.type && product.product_type) next.type = product.product_type;
      if (!next.product_type && product.product_type) next.product_type = product.product_type;
      if (!next.tags && product.tags) next.tags = product.tags;
      return next;
    } catch (error) {
      return part.role === 'coil' ? markCoilUnstocked(part) : Object.assign({}, part, { available: false });
    }
  }

  async function hydrateComboParts(combos, resolve = resolvePartVariant) {
    const byKey = new Map();
    (combos || []).forEach((combo) => {
      ['outdoor', 'furnace', 'coil', 'air_handler', 'heat_kit'].forEach((role) => {
        const part = combo[role];
        const key = partnerKey(part);
        if (part && key && !byKey.has(key)) byKey.set(key, part);
      });
    });
    const resolvedPairs = await Promise.all(
      Array.from(byKey.entries()).map(async ([key, part]) => [key, await resolve(part)])
    );
    const resolved = new Map(resolvedPairs);
    return (combos || []).map((combo) => {
      const next = Object.assign({}, combo);
      ['outdoor', 'furnace', 'coil', 'air_handler', 'heat_kit'].forEach((role) => {
        const key = partnerKey(combo[role]);
        if (key && resolved.has(key)) next[role] = resolved.get(key);
      });
      return next;
    });
  }

  async function hydratePartnerList(parts, resolve = resolvePartVariant) {
    const byKey = new Map();
    (parts || []).forEach((part) => {
      const key = partnerKey(part);
      if (part && key && !byKey.has(key)) byKey.set(key, part);
    });
    const resolvedPairs = await Promise.all(
      Array.from(byKey.entries()).map(async ([key, part]) => [key, await resolve(part)])
    );
    const resolved = new Map(resolvedPairs);
    return (parts || []).map((part) => {
      const key = partnerKey(part);
      return key && resolved.has(key) ? resolved.get(key) : part;
    });
  }

  async function addMissingVariantIds(variantIds) {
    const wanted = Array.from(
      new Set((variantIds || []).map((id) => String(id || '').trim()).filter(Boolean))
    );
    if (!wanted.length) {
      return { added: [], skipped: [], already: [], empty: true };
    }

    const cart = await fetchCart();
    const have = idsInCart(cart);
    const already = wanted.filter((id) => have.has(id));
    const toAdd = wanted.filter((id) => !have.has(id));
    const cartEl = cartRoot();
    const expectedItemCount = (cart.items || []).length + toAdd.length;

    if (!toAdd.length) {
      try {
        await refreshCartUI({ expectedItemCount, open: true });
      } catch (error) {
        // Already in cart; drawer refresh must not fail Add System.
      }
      return { added: [], skipped: [], already, empty: false };
    }

    const payload = {
      items: toAdd.map((id) => ({ id: Number(id), quantity: 1 })),
    };
    if (cartEl && typeof cartEl.setActiveElement === 'function') {
      cartEl.setActiveElement(document.activeElement);
    }

    const response = await fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (data.status) {
      throw new Error(data.description || data.message || 'Could not add system');
    }

    if (!cartEl) {
      window.location = routes.cart_url;
      return { added: toAdd, skipped: [], already, empty: false };
    }

    try {
      await refreshCartUI({ expectedItemCount, open: true });
    } catch (error) {
      // Cart add already succeeded; do not surface a section-refresh crash as an add failure.
    }

    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'ahri-system-cart',
          cartData: data,
        });
      }
    } catch (error) {
      // Subscribers may parse section HTML; a null section must not fail Add System.
    }

    return { added: toAdd, skipped: [], already, empty: false };
  }

  function statusMessage(result) {
    if (result.empty) return LABELS.empty || 'No catalog products to add for this matchup.';
    if (result.added.length && result.already.length) {
      return LABELS.addedMissing || 'Added the missing pieces to your cart.';
    }
    if (result.added.length) return LABELS.added || 'Added this system to your cart.';
    if (result.already.length) return LABELS.already || 'This system is already in your cart.';
    return LABELS.empty || 'No catalog products to add for this matchup.';
  }

  window.AhriPackageUtils.resolvePartVariant = resolvePartVariant;
  window.AhriPackageUtils.hydrateComboParts = hydrateComboParts;
  window.AhriPackageUtils.hydratePartnerList = hydratePartnerList;
  window.AhriPackageUtils.appendNoStockedCoil = appendNoStockedCoil;

  window.AhriSystemCart = {
    addMissingVariantIds,
    statusMessage,
    refreshCartUI,
    usableSectionHtml,
    pickUsableSections,
    isSectionRefreshError,
    sectionsQuery,
    drawerLineCount,
    liveDrawerLineCount,
    dropStaleSections,
    fetchDrawerHtml,
    applyBadgeCount,
  };

  async function onAddSystemButton(button) {
    if (!button) return;
    const item = button.closest('[data-ahri-item]');
    if (hostRefusesCoil(item)) {
      button.hidden = true;
      return;
    }
    if (hostMissingPartnerId(item)) {
      button.disabled = true;
      const reason = item.querySelector('[data-ahri-add-reason]');
      if (reason) reason.hidden = false;
      return;
    }
    const status = item ? item.querySelector('[data-ahri-system-status]') : null;
    const ids = item
      ? Array.from(item.querySelectorAll('[data-variant-id]')).map((el) => el.dataset.variantId)
      : [];
    button.setAttribute('aria-disabled', 'true');
    try {
      const result = await addMissingVariantIds(ids);
      if (status) {
        status.hidden = false;
        status.textContent = statusMessage(result);
      }
    } catch (error) {
      if (status) {
        status.hidden = false;
        status.textContent = isSectionRefreshError(error)
          ? statusMessage({ added: ids, skipped: [], already: [], empty: false })
          : error.message;
      }
    } finally {
      button.removeAttribute('aria-disabled');
    }
  }

  function bindAddSystemButtons(root) {
    (root || document).querySelectorAll('[data-ahri-add-system]').forEach((button) => {
      if (button.dataset.ahriBound === 'true') return;
      button.dataset.ahriBound = 'true';
      button.addEventListener('click', () => onAddSystemButton(button));
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ahri-add-system]');
    if (!button || button.dataset.ahriBound === 'true') return;
    event.preventDefault();
    onAddSystemButton(button);
  });

  function formatBtu(btu) {
    const n = Number(btu);
    if (!n) return String(btu || '');
    return `${n.toLocaleString()} BTU`;
  }

  function furnaceMeta(furnace) {
    if (!furnace) return '';
    return [
      furnace.btu ? formatBtu(furnace.btu) : '',
      furnace.afue ? `${furnace.afue}% AFUE` : '',
      furnace.width ? `${furnace.width}"` : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }

  function ahriCertLabel(combo, template) {
    if (!combo || !combo.ahri) return '';
    return (template || LABELS.ahriCert || 'AHRI cert #{{ n }} — certified combo for this outdoor + furnace').replace(
      '{{ n }}',
      combo.ahri
    );
  }

  if (!customElements.get('ahri-package-builder')) {
    customElements.define(
      'ahri-package-builder',
      class AhriPackageBuilder extends HTMLElement {
        connectedCallback() {
          const dataNode = this.querySelector('[data-ahri-package-data]');
          try {
            this.combos = JSON.parse(dataNode ? dataNode.textContent : '[]');
          } catch (e) {
            this.combos = [];
          }
          const catalogNode = this.querySelector('[data-ahri-heat-kit-catalog]');
          try {
            this.catalogHeatKits = JSON.parse(catalogNode ? catalogNode.textContent : '[]');
          } catch (e) {
            this.catalogHeatKits = [];
          }
          if (!Array.isArray(this.catalogHeatKits)) this.catalogHeatKits = [];
          this.pdpType = this.dataset.pdpType || '';
          this.pdpTitle = this.dataset.pdpTitle || '';
          this.pdpProductId = this.dataset.pdpProductId || '';
          this.pdpSku = this.dataset.pdpSku || '';
          this.dialog = this.querySelector('[data-ahri-package-dialog]');
          this.body = this.querySelector('[data-ahri-package-body]');
          this.stepLabel = this.querySelector('[data-ahri-package-step-label]');
          this.backBtn = this.querySelector('[data-ahri-package-back]');
          this.addBtn = this.querySelector('[data-ahri-package-add]');
          this.status = this.querySelector('[data-ahri-package-status]');
          this.pdpRole = this.dataset.pdpRole || 'outdoor';
          this.choice = this.blankChoice();
          this.furnaceFilters = this.blankFurnaceFilters();
          this.airHandlerFilters = this.blankPartnerFilters();
          this.coilFilters = this.blankPartnerFilters();
          this.heatKitFilters = this.blankPartnerFilters();
          this.outdoorFilters = this.blankPartnerFilters();
          this.filterOpenTier = '';
          this.filterOpenStep = '';
          this.step = this.initialStep();
          this._userOpened = false;

          ensureUnboundForm();
          applyWizardDialogClosed(this.dialog);

          const openBtn = this.querySelector('[data-ahri-package-open]');
          if (openBtn) {
            unbindFromHostForm(openBtn);
            openBtn.addEventListener('click', (event) => {
              suppressWizardFormEvent(event);
              this.open();
            });
          }
          bindWizardCloseControl(this.querySelector('[data-ahri-package-close]'), () => this.close());
          if (this.backBtn) {
            unbindFromHostForm(this.backBtn);
            this.backBtn.addEventListener('click', (event) => {
              suppressWizardFormEvent(event);
              this.goBack();
            });
          }
          if (this.addBtn) {
            unbindFromHostForm(this.addBtn);
            this.addBtn.addEventListener('click', (event) => {
              suppressWizardFormEvent(event);
              this.addChosen();
            });
          }
          if (this.dialog) {
            this.dialog.addEventListener('click', (event) => {
              if (isWizardBackdropClick(event, this.dialog)) this.close();
            });
            this.dialog.addEventListener('cancel', (event) => {
              suppressWizardFormEvent(event);
              this.close();
            });
            this.dialog.addEventListener('keydown', (event) => {
              const key = event && (event.key || event.code);
              if (key === 'Escape' || key === 'Esc') {
                suppressWizardFormEvent(event);
                this.close();
              }
            });
            this.dialog.addEventListener('close', () => {
              this._userOpened = false;
              applyWizardDialogClosed(this.dialog);
              this.releaseDialog();
            });
            this.dialog.addEventListener('submit', suppressWizardFormEvent);
          }
          this._onPageShow = () => {
            if (!this._userOpened) applyWizardDialogClosed(this.dialog);
          };
          if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener('pageshow', this._onPageShow);
          }
          this.bindPairingList();
        }

        disconnectedCallback() {
          if (typeof window !== 'undefined' && window.removeEventListener && this._onPageShow) {
            window.removeEventListener('pageshow', this._onPageShow);
          }
          if (!this.dialog) return;
          this._userOpened = false;
          applyWizardDialogClosed(this.dialog);
          if (typeof document !== 'undefined' && this.dialog.parentNode === document.body) {
            this.dialog.parentNode.removeChild(this.dialog);
          }
          this._dialogHome = null;
        }

        hostDialog() {
          if (!this.dialog || this._dialogHome) return;
          this._dialogHome = isolateWizardDialog(this.dialog);
        }

        releaseDialog() {
          restoreWizardDialog(this.dialog, this._dialogHome);
          this._dialogHome = null;
        }

        guardStepChange(renderFn) {
          if (this.body) this.body.style.pointerEvents = 'none';
          if (this.addBtn) this.addBtn.style.pointerEvents = 'none';
          renderFn();
          const unlock = () => {
            if (this.body) this.body.style.pointerEvents = '';
            if (this.addBtn) this.addBtn.style.pointerEvents = '';
          };
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => requestAnimationFrame(unlock));
          } else {
            unlock();
          }
        }

        blankChoice() {
          return {
            path: '',
            btu: '',
            afue: '',
            furnaceKey: '',
            coilKey: '',
            outdoorKey: '',
            airHandlerKey: '',
            heatKitKey: '',
            combo: null,
          };
        }

        blankFurnaceFilters() {
          return { search: '', afue: [], btu: [], width: [], series: [] };
        }

        blankPartnerFilters() {
          return {
            search: '',
            outdoorType: [],
            btu: [],
            width: [],
            series: [],
            kw: [],
            amps: [],
            kitType: [],
          };
        }

        hasFurnacePath() {
          return listableFurnaceCombos(this.combos).length > 0;
        }

        hasAirHandlerPath() {
          return airHandlerCombos(this.combos).length > 0;
        }

        hasCartableFurnacePath() {
          return listableFurnaceCombos(this.combos).some((combo) => comboCartable(combo, 'furnace'));
        }

        hasCartableAirHandlerPath() {
          return airHandlerCombos(this.combos).some((combo) => comboCartable(combo, 'air_handler'));
        }

        chosenPath() {
          if (this.pdpRole === 'air_handler' || this.choice.path === 'air_handler') return 'air_handler';
          return 'furnace';
        }

        canAddChosen() {
          return Boolean(this.choice.combo && comboCartable(this.choice.combo, this.chosenPath()));
        }

        choiceReasonText(reason) {
          if (reason === 'unavailable') return LABELS.unavailable || 'Sold out';
          if (reason === 'no_combo') return LABELS.noCartableCoil || 'No catalog coil for this furnace';
          if (reason === 'path') return LABELS.pathUnavailable || 'No catalog products for this system type';
          if (reason === 'unmapped') return LABELS.notInCatalog || 'Not in catalog';
          return '';
        }

        applyCartability(item, state) {
          return Object.assign({}, item, {
            cartable: state.cartable,
            reason: state.reason,
          });
        }

        offerableChoices(items) {
          return (items || []).filter(shouldOfferChoice);
        }

        pdpIdentity() {
          return {
            productId: this.pdpProductId || this.dataset.pdpProductId || '',
            sku: this.pdpSku || this.dataset.pdpSku || '',
          };
        }

        coilCombos() {
          return coilPackageCombos(this.combos, this.pdpIdentity());
        }

        initialStep() {
          return 'pairings';
        }

        firstStep() {
          return this.initialStep();
        }

        heatKitStepOffered() {
          return shouldOfferHeatKitStep(this.chosenOutdoor(), 'air_handler');
        }

        progressItems() {
          const heatKit = this.heatKitStepOffered()
            ? [{ key: 'heat_kit', label: LABELS.progressHeatKit || 'Heat kit' }]
            : [];
          if (this.pdpRole === 'air_handler') {
            return [
              { key: 'outdoor', label: LABELS.progressOutdoor || 'Outdoor' },
              ...heatKit,
              { key: 'summary', label: LABELS.progressPackage || 'Package' },
            ];
          }
          if (this.pdpRole === 'coil') {
            return [
              { key: 'furnace', label: LABELS.progressFurnace || 'Furnace' },
              { key: 'outdoor', label: LABELS.progressOutdoor || 'Outdoor' },
              { key: 'summary', label: LABELS.progressPackage || 'Package' },
            ];
          }
          if (this.choice.path === 'air_handler') {
            return [
              { key: 'path', label: LABELS.progressType || 'System type' },
              { key: 'air_handler', label: LABELS.progressAirHandler || 'Air handler' },
              ...heatKit,
              { key: 'summary', label: LABELS.progressPackage || 'Package' },
            ];
          }
          return [
            { key: 'path', label: LABELS.progressType || 'System type' },
            { key: 'furnace', label: LABELS.progressFurnace || 'Furnace' },
            { key: 'coil', label: LABELS.progressCoil || 'Coil' },
            { key: 'summary', label: LABELS.progressPackage || 'Package' },
          ];
        }

        progressIndex() {
          const items = this.progressItems();
          const step = this.step;
          const exact = items.findIndex((item) => item.key === step);
          if (exact >= 0) return exact;
          if (step === 'heat_kit') return Math.max(0, items.findIndex((item) => item.key === 'heat_kit'));
          return 0;
        }

        renderProgress() {
          if (!this.stepLabel) return;
          const root = this.dialog || this;
          let bar = root.querySelector('[data-ahri-package-progress]');
          if (!bar) {
            bar = document.createElement('ol');
            bar.className = 'ahri-package-progress';
            bar.setAttribute('data-ahri-package-progress', '');
            this.stepLabel.before(bar);
          }
          const items = this.progressItems();
          const active = this.progressIndex();
          bar.innerHTML = '';
          items.forEach((item, index) => {
            const li = document.createElement('li');
            li.className = `ahri-package-progress__item${index === active ? ' is-active' : ''}${
              index < active ? ' is-done' : ''
            }`;
            li.textContent = `${index + 1} ${item.label}`;
            bar.appendChild(li);
          });
        }

        open() {
          ensureUnboundForm();
          this._userOpened = true;
          this.hostDialog();
          this.choice = this.blankChoice();
          this.furnaceFilters = this.blankFurnaceFilters();
          this.airHandlerFilters = this.blankPartnerFilters();
          this.coilFilters = this.blankPartnerFilters();
          this.heatKitFilters = this.blankPartnerFilters();
          this.outdoorFilters = this.blankPartnerFilters();
          this.filterOpenTier = '';
          this.filterOpenStep = '';
          this.step = 'pairings';
          this.pairingPage = 1;
          this.pairingQuery = '';
          this.pairingPages = null;
          this.pairingTotal = null;
          this.pairingError = false;
          this.pairingLoading = false;
          this._pairingFetched = false;
          if (this._queuedSearch) this._queuedSearch.cancel();
          if (this.pairingSearch) this.pairingSearch.value = '';
          applyWizardDialogOpen(this.dialog);
          this.render();
          this.refreshPairingPage({ focus: false });
        }

        renderLoading() {
          if (this.stepLabel) {
            this.stepLabel.textContent = LABELS.checkingCatalog || 'Checking what’s in the catalog…';
          }
          if (this.body) this.body.innerHTML = '';
          if (this.addBtn) this.addBtn.hidden = true;
          if (this.backBtn) this.backBtn.hidden = true;
        }

        close() {
          this._userOpened = false;
          applyWizardDialogClosed(this.dialog);
          this.releaseDialog();
        }

        uniqueOutdoors() {
          return listUniqueOutdoors(this.combos);
        }

        chosenOutdoor() {
          if (this.choice.outdoorKey) {
            const hit = this.uniqueOutdoors().find((row) => partnerKey(row) === this.choice.outdoorKey);
            if (hit) return enrichPartner(Object.assign({}, hit, { type: hit.type || this.pdpType }));
          }
          if (this.pdpRole === 'outdoor') {
            const first = this.uniqueOutdoors()[0] || {};
            return enrichPartner(
              Object.assign({}, first, {
                title: first.title || this.pdpTitle,
                type: first.type || this.pdpType,
              })
            );
          }
          return null;
        }

        chosenAirHandler() {
          if (this.choice.airHandlerKey) {
            const hit = this.uniqueAirHandlers().find((row) => partnerKey(row) === this.choice.airHandlerKey);
            if (hit) return hit;
          }
          if (this.pdpRole === 'air_handler') {
            const first = this.uniqueAirHandlers()[0];
            if (first) {
              return enrichPartner(
                Object.assign({}, first, {
                  title: first.title || this.pdpTitle,
                  type: first.type || this.pdpType,
                })
              );
            }
            if (this.pdpTitle || this.pdpType) {
              return enrichPartner({ title: this.pdpTitle, type: this.pdpType });
            }
          }
          return null;
        }

        uniqueAirHandlers() {
          return uniqueAirHandlerPartners(this.combos);
        }

        combosForOutdoor(outdoorKey) {
          return airHandlerCombos(this.combos).filter((combo) => partnerKey(combo.outdoor) === outdoorKey);
        }

        combosForAirHandler(airHandlerKey) {
          return airHandlerCombos(this.combos).filter((combo) => partnerKey(combo.air_handler) === airHandlerKey);
        }

        heatKitOptions(filterKey, by) {
          return collectHeatKits(
            this.combos,
            filterKey,
            by,
            this.catalogHeatKits,
            this.chosenAirHandler(),
            this.chosenOutdoor()
          );
        }

        comboForAirHandler() {
          return comboForAirHandler(this.combos, this.choice, this.catalogHeatKits);
        }

        goBack() {
          if (this.choice && this.choice.fromList) {
            this.step = 'pairings';
            this.choice = this.blankChoice();
            this.render();
            if (this.pairingList && typeof this.pairingList.focus === 'function') this.pairingList.focus();
            return;
          }
          const heatKitOffered = this.heatKitStepOffered();
          if (this.pdpRole === 'air_handler') {
            if (this.step === 'summary') {
              this.step = heatKitOffered ? 'heat_kit' : 'outdoor';
            } else if (this.step === 'heat_kit') {
              this.step = 'outdoor';
            }
          } else if (this.pdpRole === 'coil') {
            if (this.step === 'summary') {
              this.step = 'outdoor';
            } else if (this.step === 'outdoor') {
              this.step = 'furnace';
            }
          } else if (this.choice.path === 'air_handler') {
            if (this.step === 'summary') {
              this.step = heatKitOffered ? 'heat_kit' : 'air_handler';
            } else if (this.step === 'heat_kit') {
              this.step = 'air_handler';
            } else if (this.step === 'air_handler') {
              this.step = 'path';
            }
          } else if (this.step === 'summary') {
            this.step = 'coil';
          } else if (this.step === 'coil') {
            this.step = 'furnace';
          } else if (this.step === 'furnace') {
            this.step = 'path';
          }
          this.render();
        }

        renderChoiceList(items, selectedKey, onPick, titleFn, metaFn) {
          const list = document.createElement('div');
          list.className = 'ahri-package-dialog__coils';
          items.forEach((item) => {
            const key = typeof item === 'string' ? item : item.key;
            const cartable = !item || item.cartable !== false;
            const button = document.createElement('button');
            button.type = 'button';
            unbindFromHostForm(button);
            button.className = `ahri-package-dialog__choice${selectedKey === key ? ' is-active' : ''}${
              cartable ? '' : ' is-disabled'
            }`;
            const title = document.createElement('strong');
            title.textContent = titleFn(item);
            button.appendChild(title);
            const meta = metaFn ? metaFn(item) : '';
            if (meta) {
              const span = document.createElement('span');
              span.textContent = meta;
              button.appendChild(span);
            }
            const reasonText = !cartable ? this.choiceReasonText(item && item.reason) : '';
            if (reasonText) {
              const reason = document.createElement('span');
              reason.className = 'ahri-package-dialog__choice-reason';
              reason.textContent = reasonText;
              button.appendChild(reason);
            }
            if (!cartable) {
              button.disabled = true;
              button.setAttribute('aria-disabled', 'true');
            } else {
              button.addEventListener('click', (event) => {
                suppressWizardFormEvent(event);
                onPick(item);
              });
            }
            list.appendChild(button);
          });
          return list;
        }

        chipGroupLabels() {
          return {
            afue: LABELS.filterAfue || 'AFUE',
            btu: LABELS.filterCapacity || LABELS.filterBtu || 'BTU / tonnage',
            width: LABELS.filterWidth || 'Width',
            series: LABELS.filterSeries || 'Series',
            kw: LABELS.filterKw || 'kW',
            amps: LABELS.filterAmps || 'Amps',
            kitType: LABELS.filterType || 'Type',
            outdoorType: LABELS.filterType || 'Type',
          };
        }

        resetFilterAccordion() {
          this.filterOpenTier = '';
          this.filterOpenStep = '';
        }

        ensureFilterOpenTier(groups) {
          if (this.filterOpenStep !== this.step) {
            this.filterOpenStep = this.step;
            this.filterOpenTier = firstFilterTier(groups);
            return;
          }
          this.filterOpenTier = resolveOpenFilterTier(groups, this.filterOpenTier);
        }

        // Search/filter UX: matching-options dropdown and one-click clear.
        renderPickerStep(options) {
          const {
            items,
            filters,
            groups,
            onSearch,
            onToggle,
            searchLabel,
            searchPlaceholder,
            countText,
            emptyText,
            selectedKey,
            onPick,
            titleFn,
            metaFn,
            prependItems,
          } = options;
          const wrap = document.createElement('div');
          wrap.className = 'ahri-package-furnace';

          const query = searchQuery(filters);
          const applySearch = (value) => {
            onSearch(value);
            this.render();
            const again = this.body.querySelector('.ahri-package-furnace__search input');
            if (again) {
              again.focus();
              const end = again.value.length;
              if (typeof again.setSelectionRange === 'function') again.setSelectionRange(end, end);
            }
          };

          const searchWrap = document.createElement('div');
          searchWrap.className = `ahri-package-furnace__search${query ? ' is-searching' : ''}`;
          const searchCaption = document.createElement('label');
          searchCaption.className = 'ahri-package-furnace__search-caption';
          searchCaption.textContent = searchLabel || LABELS.furnaceSearch || 'Search model or SKU';
          const searchField = document.createElement('div');
          searchField.className = 'ahri-package-furnace__search-field';
          const search = document.createElement('input');
          search.type = 'search';
          search.id = 'ahri-package-picker-search';
          searchCaption.setAttribute('for', search.id);
          unbindFromHostForm(search);
          search.className = 'ahri-list-controls__input';
          search.value = (filters && filters.search) || '';
          search.placeholder = searchPlaceholder || '';
          search.setAttribute('role', 'combobox');
          search.setAttribute('aria-expanded', query ? 'true' : 'false');
          search.setAttribute('aria-controls', 'ahri-package-search-options');
          search.setAttribute('aria-autocomplete', 'list');
          search.autocomplete = 'off';
          search.addEventListener('input', () => applySearch(search.value));
          search.addEventListener('search', suppressWizardFormEvent);
          search.addEventListener('keydown', (event) => {
            if (isWizardFormSubmitKey(event)) suppressWizardFormEvent(event);
            if (event.key === 'Escape' && search.value) {
              event.preventDefault();
              applySearch('');
            }
          });
          searchField.appendChild(search);
          if (query) {
            const clear = document.createElement('button');
            clear.type = 'button';
            clear.className = 'ahri-package-furnace__search-clear';
            clear.setAttribute('aria-label', LABELS.searchClear || 'Clear search');
            clear.textContent = LABELS.searchClear || 'Clear search';
            clear.addEventListener('click', () => applySearch(''));
            searchField.appendChild(clear);
          }
          searchWrap.appendChild(searchCaption);
          searchWrap.appendChild(searchField);

          if (query) {
            const drop = document.createElement('div');
            drop.className = 'ahri-package-furnace__search-drop';
            drop.id = 'ahri-package-search-options';
            drop.setAttribute('role', 'listbox');
            drop.setAttribute('aria-label', LABELS.searchResults || 'Matching options');
            if (items.length) {
              items.forEach((item) => {
                const option = document.createElement('button');
                option.type = 'button';
                option.className = 'ahri-package-furnace__search-option';
                option.setAttribute('role', 'option');
                const title = document.createElement('strong');
                title.textContent = titleFn(item);
                option.appendChild(title);
                const meta = metaFn ? metaFn(item) : '';
                if (meta) {
                  const span = document.createElement('span');
                  span.textContent = meta;
                  option.appendChild(span);
                }
                option.addEventListener('click', () => onPick(item));
                drop.appendChild(option);
              });
            } else {
              const none = document.createElement('p');
              none.className = 'ahri-package-furnace__search-none';
              none.textContent = pickerSearchEmpty(emptyText, LABELS.searchEmpty, query);
              drop.appendChild(none);
              const dropClear = document.createElement('button');
              dropClear.type = 'button';
              dropClear.className = 'ahri-package-furnace__search-clear-btn';
              dropClear.textContent = LABELS.searchClear || 'Clear search';
              dropClear.addEventListener('click', () => applySearch(''));
              drop.appendChild(dropClear);
            }
            searchWrap.appendChild(drop);
          }

          wrap.appendChild(searchWrap);

          const groupsEl = document.createElement('div');
          groupsEl.className = 'ahri-package-furnace__groups';
          wrap.appendChild(groupsEl);

          const chipLabels = this.chipGroupLabels();
          this.ensureFilterOpenTier(groups);
          (groups || []).forEach((group) => {
            if (!group.items.length) return;
            const selectedLabels = group.items
              .filter((item) => ((filters && filters[group.key]) || []).includes(item.key))
              .map((item) => item.label);
            const isOpen = this.filterOpenTier === group.key;
            const row = document.createElement('div');
            row.className = `ahri-package-furnace__chips${isOpen ? ' is-open' : ''}`;
            const toggle = document.createElement('button');
            toggle.type = 'button';
            unbindFromHostForm(toggle);
            toggle.className = 'ahri-package-furnace__accordion';
            toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            const caption = document.createElement('span');
            caption.className = 'ahri-package-furnace__chip-label';
            caption.textContent = filterGroupSummary(chipLabels[group.key] || group.key, selectedLabels);
            const chevron = document.createElement('span');
            chevron.className = 'ahri-package-furnace__chevron';
            chevron.setAttribute('aria-hidden', 'true');
            toggle.appendChild(caption);
            toggle.appendChild(chevron);
            toggle.addEventListener('click', (event) => {
              suppressWizardFormEvent(event);
              this.filterOpenTier = isOpen ? '' : group.key;
              this.filterOpenStep = this.step;
              this.render();
            });
            row.appendChild(toggle);
            const list = document.createElement('div');
            list.className = 'ahri-filters__chips';
            group.items.forEach((item) => {
              const selected = ((filters && filters[group.key]) || []).includes(item.key);
              const button = document.createElement('button');
              button.type = 'button';
              unbindFromHostForm(button);
              button.className = `ahri-filter${selected ? ' is-active' : ''}${item.enabled ? '' : ' is-disabled'}`;
              button.setAttribute('aria-pressed', selected ? 'true' : 'false');
              button.disabled = !item.enabled;
              const span = document.createElement('span');
              span.textContent = item.label;
              button.appendChild(span);
              button.addEventListener('click', (event) => {
                suppressWizardFormEvent(event);
                if (!item.enabled) return;
                onToggle(group.key, item.key);
                const nowSelected = ((filters && filters[group.key]) || []).includes(item.key);
                if (nowSelected) {
                  this.filterOpenTier = openTierAfterSelect(groups, group.key);
                  this.filterOpenStep = this.step;
                }
                this.render();
              });
              list.appendChild(button);
            });
            row.appendChild(list);
            groupsEl.appendChild(row);
          });

          const count = document.createElement('p');
          count.className = 'ahri-package-furnace__count';
          count.textContent = pickerSearchCount(countText, LABELS.searchCount, items.length, query);
          wrap.appendChild(count);

          if (prependItems && prependItems.length) {
            wrap.appendChild(
              this.renderChoiceList(prependItems, selectedKey, onPick, titleFn, metaFn)
            );
          }

          if (!items.length) {
            if (!query) {
              const empty = document.createElement('p');
              empty.className = 'ahri-package-furnace__empty';
              empty.textContent = emptyText;
              wrap.appendChild(empty);
            }
            this.body.appendChild(wrap);
            return;
          }

          wrap.appendChild(this.renderChoiceList(items, selectedKey, onPick, titleFn, metaFn));
          this.body.appendChild(wrap);
        }

        toggleFilterValue(store, key, value) {
          const current = new Set((store && store[key]) || []);
          if (current.has(value)) current.delete(value);
          else current.add(value);
          store[key] = Array.from(current);
          return store;
        }

        pairingApi() {
          return (typeof window !== 'undefined' && window.AhriPackagePage) || null;
        }

        bindPairingList() {
          if (this._pairingBound) return;
          this._pairingBound = true;
          this.pairingPanel = this.querySelector('[data-ahri-package-pairings]');
          this.pairingList = this.querySelector('[data-ahri-package-list]');
          this.pairingPageEl = this.querySelector('[data-ahri-package-page]');
          this.pairingEmpty = this.querySelector('[data-ahri-package-empty]');
          this.pairingRetry = this.querySelector('[data-ahri-package-retry]');
          this.pairingPrev = this.querySelector('[data-ahri-package-prev]');
          this.pairingNext = this.querySelector('[data-ahri-package-next]');
          this.pairingSearch = this.querySelector('[data-ahri-package-search]');
          this.pairingRetryBtn = this.querySelector('[data-ahri-package-retry-btn]');
          this.pairingPage = 1;
          this.pairingQuery = '';
          this.pairingPages = null;
          this.pairingTotal = null;
          this.pairingLoading = false;
          this.pairingError = false;
          this._pairingFetched = false;
          this._pairingToken = 0;
          this._pairingHoldCards = false;
          const api = this.pairingApi();
          const wait = (api && api.SEARCH_DEBOUNCE_MS) || 300;
          const debounceFn =
            (api && api.debounce) ||
            function fallbackDebounce(fn, delay) {
              let generation = 0;
              function debounced(value) {
                generation += 1;
                const current = generation;
                setTimeout(() => {
                  if (current !== generation) return;
                  fn(value);
                }, delay);
              }
              debounced.cancel = function cancel() {
                generation += 1;
              };
              return debounced;
            };
          this._queuedSearch = debounceFn((value) => {
            this.pairingQuery = String(value || '').replace(/\|\|\|/g, ' ').trim();
            this.pairingPage = 1;
            this.refreshPairingPage({ focus: false });
          }, wait);
          if (this.pairingSearch) {
            this.pairingSearch.addEventListener('input', () => this._queuedSearch(this.pairingSearch.value));
            this.pairingSearch.addEventListener('search', (event) => {
              suppressWizardFormEvent(event);
              this._queuedSearch(this.pairingSearch.value);
            });
            this.pairingSearch.addEventListener('keydown', (event) => {
              if (isWizardFormSubmitKey(event)) suppressWizardFormEvent(event);
            });
          }
          if (this.pairingPrev) {
            unbindFromHostForm(this.pairingPrev);
            this.pairingPrev.addEventListener('click', (event) => {
              suppressWizardFormEvent(event);
              if (this.pairingPrev.disabled) return;
              this.pairingPage = Math.max(1, (this.pairingPage || 1) - 1);
              this.refreshPairingPage({ focus: true });
            });
          }
          if (this.pairingNext) {
            unbindFromHostForm(this.pairingNext);
            this.pairingNext.addEventListener('click', (event) => {
              suppressWizardFormEvent(event);
              if (this.pairingNext.disabled) return;
              this.pairingPage = (this.pairingPage || 1) + 1;
              this.refreshPairingPage({ focus: true });
            });
          }
          if (this.pairingRetryBtn) {
            unbindFromHostForm(this.pairingRetryBtn);
            this.pairingRetryBtn.addEventListener('click', (event) => {
              suppressWizardFormEvent(event);
              this.refreshPairingPage({ focus: true });
            });
          }
        }

        paintPairingChrome() {
          const api = this.pairingApi();
          const page = this.pairingPage || 1;
          const pages = this.pairingPages;
          const loading = Boolean(this.pairingLoading);
          const pager = api
            ? api.pagerState(page, pages, loading)
            : { prevDisabled: true, nextDisabled: true };
          if (this.pairingPrev) this.pairingPrev.disabled = pager.prevDisabled;
          if (this.pairingNext) this.pairingNext.disabled = pager.nextDisabled;
          const showEmpty = !loading && !this.pairingError && this.pairingTotal === 0;
          const showRetry = Boolean(this.pairingError);
          if (this.pairingEmpty) this.pairingEmpty.hidden = !showEmpty;
          if (this.pairingRetry) this.pairingRetry.hidden = !showRetry;
          if (!this.pairingPageEl) return;
          if (showEmpty || showRetry) {
            this.pairingPageEl.hidden = true;
            return;
          }
          this.pairingPageEl.hidden = false;
          if (loading && !this._pairingHoldCards) {
            this.pairingPageEl.textContent = LABELS.pageLoading || 'Loading pairings';
            return;
          }
          if (pages == null) {
            this.pairingPageEl.textContent = '';
            return;
          }
          const text = api
            ? api.statusText(
                {
                  page: LABELS.pageStatus,
                  count: LABELS.pageCount,
                  one: LABELS.pageOne,
                },
                { page, pages, total: this.pairingTotal }
              )
            : '';
          this.pairingPageEl.textContent = text;
        }

        renderPairingList() {
          if (this.stepLabel) this.stepLabel.textContent = LABELS.pairingStep || 'Choose a matched system';
          const list = this.pairingList;
          const skeleton = Boolean(this.pairingLoading) && !this._pairingHoldCards;
          if (list) {
            list.replaceChildren();
            list.classList.toggle('is-loading', skeleton);
            list.setAttribute('aria-busy', this.pairingLoading ? 'true' : 'false');
            if (skeleton) {
              const bones = Number(this.dataset.pageSize) || (this.pairingApi() && this.pairingApi().PAGE_SIZE) || 5;
              for (let i = 0; i < bones; i += 1) {
                const bone = document.createElement('div');
                bone.className = 'ahri-package-pairings__bone';
                bone.setAttribute('aria-hidden', 'true');
                list.appendChild(bone);
              }
            } else if (!this.pairingError || (this.combos || []).length) {
              if (this.pairingTotal !== 0) {
                (this.combos || []).forEach((combo) => list.appendChild(this.pairingItem(combo)));
              }
            }
          }
          this.paintPairingChrome();
        }

        pairingItem(combo) {
          const item = document.createElement('div');
          item.setAttribute('role', 'listitem');
          const btn = document.createElement('button');
          btn.type = 'button';
          unbindFromHostForm(btn);
          btn.className = 'ahri-package-pairings__item';
          const ahri = document.createElement('span');
          ahri.className = 'ahri-package-pairings__ahri';
          ahri.textContent = String(LABELS.pairingAhri || 'AHRI {{ n }}').replace(
            '{{ n }}',
            String((combo && combo.ahri) || '')
          );
          btn.appendChild(ahri);
          const names = [];
          if (combo && combo.furnace && combo.furnace.title) names.push(combo.furnace.title);
          if (combo && combo.air_handler && combo.air_handler.title) names.push(combo.air_handler.title);
          if (combo && combo.coil && combo.coil.title) names.push(combo.coil.title);
          if (names.length) {
            const meta = document.createElement('span');
            meta.className = 'ahri-package-pairings__meta';
            meta.textContent = names.join(', ');
            btn.appendChild(meta);
          }
          btn.setAttribute(
            'aria-label',
            [LABELS.pairingChoose || 'Choose this pairing', ahri.textContent].concat(names).join(', ')
          );
          btn.addEventListener('click', (event) => {
            suppressWizardFormEvent(event);
            this.selectPairing(combo);
          });
          item.appendChild(btn);
          return item;
        }

        async selectPairing(combo) {
          this.choice = this.blankChoice();
          this.choice.fromList = true;
          this.choice.path = combo && combo.air_handler && !combo.furnace ? 'air_handler' : 'furnace';
          this.choice.combo = combo;
          this.step = 'summary';
          this.render();
          try {
            const hydrated = await hydrateComboParts([combo]);
            if (this.step !== 'summary' || !this.choice || this.choice.combo !== combo) return;
            this.choice.combo = (hydrated && hydrated[0]) || combo;
            this.render();
          } catch (error) {
            console.error('ahri package pairing hydrate failed');
          }
        }

        async refreshPairingPage(options) {
          const opts = options || {};
          const api = this.pairingApi();
          const handle = this.dataset.productHandle || '';
          const page = this.pairingPage || 1;
          const query = this.pairingQuery || '';
          const token = (this._pairingToken || 0) + 1;
          this._pairingToken = token;
          this._pairingHoldCards =
            page === 1 && query === '' && !this._pairingFetched && (this.combos || []).length > 0;
          this.pairingLoading = true;
          this.pairingError = false;
          if (this.step === 'pairings') this.renderPairingList();
          if (!handle || !api) {
            this.pairingLoading = false;
            this._pairingHoldCards = false;
            this.pairingError = true;
            if (this.step === 'pairings') this.renderPairingList();
            return;
          }
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const url = `${root}search?view=ahri-package-page&q=${encodeURIComponent(api.requestToken(handle, page, query))}`;
          try {
            const res = await fetch(url, { headers: { Accept: 'application/json' } });
            if (!res.ok) throw new Error(String(res.status));
            const data = await res.json();
            if (token !== this._pairingToken) return;
            this.combos = Array.isArray(data.combos) ? data.combos : [];
            this.pairingPage = Number(data.page) || page;
            this.pairingPages = Number(data.pages) || 0;
            this.pairingTotal = Number(data.total) || 0;
            this.pairingLoading = false;
            this.pairingError = false;
            this._pairingHoldCards = false;
            this._pairingFetched = true;
            if (this.step === 'pairings') {
              this.renderPairingList();
              if (opts.focus && this.pairingList && typeof this.pairingList.focus === 'function') {
                this.pairingList.focus();
              }
            }
            hydrateComboParts(this.combos)
              .then((hydrated) => {
                if (token !== this._pairingToken || this.step !== 'pairings') return;
                this.combos = hydrated;
                this.renderPairingList();
              })
              .catch(() => {
                console.error('ahri package page hydrate failed');
              });
          } catch (error) {
            if (token !== this._pairingToken) return;
            console.error('ahri package page failed');
            this.pairingLoading = false;
            this._pairingHoldCards = false;
            this.pairingError = true;
            if (this.step === 'pairings') {
              this.renderPairingList();
              if (opts.focus && this.pairingRetryBtn && typeof this.pairingRetryBtn.focus === 'function') {
                this.pairingRetryBtn.focus();
              }
            }
          }
        }

        render() {
          if (!this.body) return;
          this.body.innerHTML = '';
          if (this.status) this.status.hidden = true;
          if (this.pairingPanel) this.pairingPanel.hidden = this.step !== 'pairings';
          if (this.step === 'pairings') {
            this.renderPairingList();
            if (this.backBtn) this.backBtn.hidden = true;
            if (this.addBtn) this.addBtn.hidden = true;
            return;
          }
          if (this.choice && this.choice.fromList) {
            const bar = (this.dialog || this).querySelector('[data-ahri-package-progress]');
            if (bar) bar.remove();
          } else {
            this.renderProgress();
          }

          if (this.pdpRole === 'air_handler') {
            this.renderAirHandlerFromAhPdp();
            this.backBtn.hidden = this.step === 'outdoor';
            this.addBtn.hidden = this.step !== 'summary' || !this.canAddChosen();
            return;
          }

          if (this.pdpRole === 'coil') {
            this.renderCoilFromCoilPdp();
            this.backBtn.hidden = this.step === 'furnace';
            this.addBtn.hidden = this.step !== 'summary' || !this.canAddChosen();
            return;
          }

          if (this.step === 'path') {
            this.stepLabel.textContent = LABELS.stepPath || 'Step 1 — Choose a system type';
            const paths = [
              {
                key: 'furnace',
                title: LABELS.pathFurnace || 'Gas furnace + coil',
                cartable: this.hasCartableFurnacePath(),
                reason: this.hasCartableFurnacePath() ? '' : 'path',
              },
              {
                key: 'air_handler',
                title: LABELS.pathAirHandler || 'Air handler',
                cartable: this.hasCartableAirHandlerPath(),
                reason: this.hasCartableAirHandlerPath() ? '' : 'path',
              },
            ].filter((item) => {
              if (item.key === 'furnace') return this.hasFurnacePath() || this.hasCartableFurnacePath();
              return this.hasAirHandlerPath() || this.hasCartableAirHandlerPath();
            });
            const visible = paths.length
              ? paths
              : [
                  { key: 'furnace', title: LABELS.pathFurnace || 'Gas furnace + coil', cartable: false, reason: 'path' },
                  {
                    key: 'air_handler',
                    title: LABELS.pathAirHandler || 'Air handler',
                    cartable: false,
                    reason: 'path',
                  },
                ];
            this.body.appendChild(
              this.renderChoiceList(
                visible,
                this.choice.path,
                (item) => {
                  if (item.cartable === false) return;
                  this.choice = this.blankChoice();
                  this.furnaceFilters = this.blankFurnaceFilters();
                  this.airHandlerFilters = this.blankPartnerFilters();
                  this.coilFilters = this.blankPartnerFilters();
                  this.heatKitFilters = this.blankPartnerFilters();
                  this.outdoorFilters = this.blankPartnerFilters();
                  this.resetFilterAccordion();
                  this.choice.path = item.key;
                  this.step = item.key === 'air_handler' ? 'air_handler' : 'furnace';
                  this.render();
                },
                (item) => item.title
              )
            );
          } else if (this.choice.path === 'air_handler' || this.step === 'air_handler' || this.step === 'heat_kit') {
            this.renderOutdoorAirHandler();
          } else if (this.step === 'furnace') {
            this.renderFurnaceStep();
          } else if (this.step === 'coil') {
            this.renderCoilStep();
          } else if (this.step === 'summary') {
            this.renderSummary(['Outdoor', 'Furnace', 'Coil', 'Air Handler', 'Heat kit']);
          }

          this.backBtn.hidden = this.step === this.firstStep();
          this.addBtn.hidden = this.step !== 'summary' || !this.canAddChosen();
        }

        renderFurnaceStep(options) {
          const opts = options || {};
          const combos = opts.combos || this.combos;
          this.stepLabel.textContent = opts.stepLabel || LABELS.stepFurnace || 'Step 2 — Choose a furnace';
          const furnaces = uniqueFurnaces(combos);
          if (!furnaces.length) {
            this.body.textContent =
              opts.noFurnacesText ||
              LABELS.noBtu ||
              'No furnace in these AHRI combos has a width-matched coil for this outdoor.';
            return;
          }
          const cascaded = cascadeFurnaceFilters(furnaces, this.furnaceFilters || this.blankFurnaceFilters());
          this.furnaceFilters = cascaded.filters;
          const filtered = filterFurnaces(furnaces, this.furnaceFilters);
          const visible = this.offerableChoices(
            filtered.map((furnace) =>
              this.applyCartability({ key: furnaceKey(furnace), furnace }, furnaceChoiceState(furnace, combos))
            )
          );
          this.renderPickerStep({
            items: visible,
            filters: this.furnaceFilters,
            groups: cascaded.groups,
            onSearch: (value) => {
              this.furnaceFilters.search = value;
            },
            onToggle: (tier, value) => {
              this.furnaceFilters = pruneFurnaceFilters(
                furnaces,
                this.toggleFilterValue(this.furnaceFilters, tier, value)
              );
            },
            searchPlaceholder: LABELS.furnaceSearchPh || 'ML196, EL280, SKU…',
            countText: (LABELS.furnaceCount || '{{ n }} furnaces').replace('{{ n }}', String(visible.length)),
            emptyText:
              filtered.length && !visible.length
                ? LABELS.pathUnavailable || 'No catalog products for this system type'
                : LABELS.furnaceEmpty ||
                  'No furnaces match these filters. Clear a chip or search to see more.',
            selectedKey: this.choice.furnaceKey,
            onPick: (item) => {
              if (item.cartable === false) return;
              const next = applyFurnacePick(this.choice, item.furnace, opts.nextStep);
              this.choice = next.choice;
              this.coilFilters = this.blankPartnerFilters();
              this.outdoorFilters = this.blankPartnerFilters();
              this.resetFilterAccordion();
              this.step = next.step;
              this.guardStepChange(() => this.render());
            },
            titleFn: (item) => item.furnace.title || partnerKey(item.furnace) || 'Furnace',
            metaFn: (item) => furnaceMeta(item.furnace),
          });
        }

        renderCoilStep() {
          this.stepLabel.textContent = LABELS.stepCoil || 'Step 3 — Choose a width-matched coil';
          const matches = listableFurnaceCombos(this.combos)
            .filter((combo) => {
              if (this.choice.furnaceKey) return furnaceKey(combo.furnace) === this.choice.furnaceKey;
              if (this.choice.btu && combo.furnace.btu !== this.choice.btu) return false;
              if (this.choice.afue && combo.furnace.afue && combo.furnace.afue !== this.choice.afue) return false;
              return true;
            })
            .map((combo) => Object.assign({}, combo, { coil: enrichPartner(combo.coil) }));
          if (!matches.length) {
            this.body.textContent =
              LABELS.noCoil ||
              'No coil in these AHRI combos matches the chosen furnace cabinet width.';
            return;
          }
          const stocked = matches.filter((combo) => !coilRefused(combo.coil));
          if (!stocked.length) {
            const note = document.createElement('p');
            note.className = 'ahri-package-dialog__status';
            appendNoStockedCoil(note);
            this.body.appendChild(note);
            return;
          }
          const coils = stocked.map((combo) => combo.coil).filter(Boolean);
          const applied = applyPartnerFilters(
            coils,
            this.coilFilters || this.blankPartnerFilters(),
            COIL_FILTER_TIERS
          );
          this.coilFilters = applied.filters;
          const filteredCoils = applied.filtered;
          const visibleCoils = new Set(filteredCoils.map((coil) => partnerKey(coil)));
          const visible = this.offerableChoices(
            stocked
              .filter((combo) => visibleCoils.has(partnerKey(combo.coil)))
              .map((combo) =>
                this.applyCartability(
                  {
                    key: `${combo.ahri}-${combo.coil && combo.coil.handle}`,
                    combo,
                  },
                  partnerChoiceState(combo.coil)
                )
              )
          );
          this.renderPickerStep({
            items: visible,
            filters: this.coilFilters,
            groups: applied.groups,
            onSearch: (value) => {
              this.coilFilters.search = value;
            },
            onToggle: (tier, value) => {
              this.coilFilters = prunePartnerFilters(
                applied.pool,
                this.toggleFilterValue(this.coilFilters, tier, value),
                COIL_FILTER_TIERS
              );
            },
            searchPlaceholder: LABELS.coilSearchPh || 'CK40, CX35, SKU…',
            countText: (LABELS.coilCount || '{{ n }} coils').replace('{{ n }}', String(visible.length)),
            emptyText:
              filteredCoils.length && !visible.length
                ? LABELS.pathUnavailable || 'No catalog products for this system type'
                : LABELS.coilEmpty || 'No coils match these filters. Clear a chip or search to see more.',
            selectedKey: this.choice.coilKey,
            onPick: (item) => {
              if (item.cartable === false) return;
              this.choice.coilKey = item.key;
              this.choice.combo = item.combo;
              this.step = 'summary';
              this.render();
            },
            titleFn: (item) => (item.combo.coil && item.combo.coil.title) || 'Coil',
            metaFn: (item) => coilMeta(item.combo.coil, item.combo),
          });
        }

        renderLockedCoilNote() {
          const title = this.pdpTitle || '';
          const note = document.createElement('p');
          note.className = 'ahri-package-furnace__note';
          note.textContent = applySearchTokens(LABELS.coilLocked || 'This coil is locked: {{ title }}', { title });
          this.body.appendChild(note);
        }

        renderCoilFromCoilPdp() {
          const combos = this.coilCombos();
          if (!combos.length) {
            this.stepLabel.textContent = LABELS.stepFurnaceFromCoil || 'Step 1 — Choose a furnace';
            this.body.textContent =
              LABELS.noFurnaceForCoil || 'No furnaces were found on this coil’s AHRI matchups.';
            return;
          }
          this.renderLockedCoilNote();
          if (this.step === 'furnace') {
            this.renderFurnaceStep({
              combos,
              nextStep: 'outdoor',
              stepLabel: LABELS.stepFurnaceFromCoil || 'Step 1 — Choose a furnace',
              noFurnacesText:
                LABELS.noFurnaceForCoil || 'No furnaces were found on this coil’s AHRI matchups.',
            });
            return;
          }
          if (this.step === 'outdoor') {
            this.renderCoilOutdoorStep(combos);
            return;
          }
          this.choice.combo = this.choice.combo || comboForCoilPackage(this.combos, this.choice, this.pdpIdentity());
          this.renderSummary(['Coil', 'Furnace', 'Outdoor']);
        }

        renderCoilOutdoorStep(combos) {
          this.stepLabel.textContent = LABELS.stepOutdoorFromCoil || 'Step 2 — Choose an outdoor unit';
          const matches = (combos || this.coilCombos()).filter((combo) => {
            if (this.choice.furnaceKey) return furnaceKey(combo.furnace) === this.choice.furnaceKey;
            return true;
          });
          const outdoors = uniqueOutdoorPartners(matches);
          if (!outdoors.length) {
            this.body.textContent =
              LABELS.noOutdoor || 'No outdoor units were found on this product’s AHRI matchups.';
            return;
          }
          const applied = applyPartnerFilters(
            outdoors,
            this.outdoorFilters || this.blankPartnerFilters(),
            OUTDOOR_FILTER_TIERS
          );
          if (!applied.pool.length) {
            this.body.textContent =
              LABELS.pathUnavailable || 'No catalog products for this system type';
            return;
          }
          this.outdoorFilters = applied.filters;
          const filtered = applied.filtered;
          const visible = this.offerableChoices(
            filtered.map((outdoor) => {
              const key = partnerKey(outdoor);
              let state = partnerChoiceState(outdoor);
              if (state.cartable) {
                const ok = matches.some(
                  (combo) => partnerKey(combo.outdoor) === key && comboCartable(combo, 'furnace')
                );
                if (!ok) state = { cartable: false, reason: 'path' };
              }
              return this.applyCartability({ key, outdoor }, state);
            })
          );
          this.renderPickerStep({
            items: visible,
            filters: this.outdoorFilters,
            groups: applied.groups,
            onSearch: (value) => {
              this.outdoorFilters.search = value;
            },
            onToggle: (tier, value) => {
              this.outdoorFilters = prunePartnerFilters(
                applied.pool,
                this.toggleFilterValue(this.outdoorFilters, tier, value),
                OUTDOOR_FILTER_TIERS
              );
            },
            searchPlaceholder: LABELS.outdoorSearchPh || 'EL22, ML13, SKU…',
            countText: (LABELS.outdoorCount || '{{ n }} outdoor units').replace(
              '{{ n }}',
              String(visible.length)
            ),
            emptyText:
              filtered.length && !visible.length
                ? LABELS.pathUnavailable || 'No catalog products for this system type'
                : LABELS.outdoorEmpty ||
                  'No outdoor units match these filters. Clear a chip or search to see more.',
            selectedKey: this.choice.outdoorKey,
            onPick: (item) => {
              if (item.cartable === false) return;
              this.choice.outdoorKey = item.key;
              this.choice.combo = comboForCoilPackage(this.combos, this.choice, this.pdpIdentity());
              this.step = 'summary';
              this.render();
            },
            titleFn: (item) => item.outdoor.title || item.key,
            metaFn: (item) => outdoorMeta(item.outdoor),
          });
        }

        renderOutdoorAirHandler() {
          if (this.step === 'air_handler') {
            this.stepLabel.textContent = LABELS.stepPickAh || 'Step 2 — Choose an air handler';
            const handlers = this.uniqueAirHandlers();
            if (!handlers.length) {
              this.body.textContent =
                LABELS.noAirHandler ||
                'No air handlers were found on this product’s AHRI matchups.';
              return;
            }
            const applied = applyPartnerFilters(
              handlers,
              this.airHandlerFilters || this.blankPartnerFilters(),
              AIR_HANDLER_FILTER_TIERS
            );
            if (!applied.pool.length) {
              this.body.textContent =
                LABELS.pathUnavailable || 'No catalog products for this system type';
              return;
            }
            this.airHandlerFilters = applied.filters;
            const filtered = applied.filtered;
            const visible = this.offerableChoices(
              filtered.map((ah) => {
                const key = partnerKey(ah);
                let state = partnerChoiceState(ah);
                if (state.cartable) {
                  const ok = this.combosForAirHandler(key).some((combo) => comboCartable(combo, 'air_handler'));
                  if (!ok) state = { cartable: false, reason: 'path' };
                }
                return this.applyCartability({ key, ah }, state);
              })
            );
            this.renderPickerStep({
              items: visible,
              filters: this.airHandlerFilters,
              groups: applied.groups,
              onSearch: (value) => {
                this.airHandlerFilters.search = value;
              },
              onToggle: (tier, value) => {
                this.airHandlerFilters = prunePartnerFilters(
                  applied.pool,
                  this.toggleFilterValue(this.airHandlerFilters, tier, value),
                  AIR_HANDLER_FILTER_TIERS
                );
              },
              searchPlaceholder: LABELS.ahSearchPh || 'CBK45, CBA25, SKU…',
              countText: (LABELS.ahCount || '{{ n }} air handlers').replace('{{ n }}', String(visible.length)),
              emptyText:
                filtered.length && !visible.length
                  ? LABELS.pathUnavailable || 'No catalog products for this system type'
                  : LABELS.ahEmpty || 'No air handlers match these filters. Clear a chip or search to see more.',
              selectedKey: this.choice.airHandlerKey,
              onPick: (item) => {
                if (item.cartable === false) return;
                this.choice.airHandlerKey = item.key;
                this.choice.heatKitKey = '';
                this.choice.combo = null;
                this.heatKitFilters = this.blankPartnerFilters();
                this.resetFilterAccordion();
                this.step = stepAfterAirHandlerPartner(
                  this.heatKitOptions(item.key, 'air_handler'),
                  this.chosenOutdoor(),
                  'air_handler'
                );
                if (this.step === 'summary') this.choice.combo = this.comboForAirHandler();
                this.render();
              },
              titleFn: (item) => item.ah.title || item.key,
              metaFn: (item) => airHandlerMeta(item.ah),
            });
            return;
          }

          if (this.step === 'heat_kit') {
            this.renderHeatKitStep(this.choice.airHandlerKey, 'air_handler');
            return;
          }

          this.choice.combo = this.choice.combo || this.comboForAirHandler();
          this.renderSummary(['Outdoor', 'Air Handler', 'Heat kit']);
        }

        renderAirHandlerFromAhPdp() {
          if (this.step === 'outdoor') {
            this.stepLabel.textContent = LABELS.stepOutdoor || 'Step 1 — Choose an outdoor unit';
            const outdoors = this.uniqueOutdoors();
            if (!outdoors.length) {
              this.body.textContent =
                LABELS.noOutdoor || 'No outdoor units were found on this product’s AHRI matchups.';
              return;
            }
            const applied = applyPartnerFilters(
              outdoors,
              this.outdoorFilters || this.blankPartnerFilters(),
              AH_OUTDOOR_FILTER_TIERS
            );
            if (!applied.pool.length) {
              this.body.textContent =
                LABELS.pathUnavailable || 'No catalog products for this system type';
              return;
            }
            this.outdoorFilters = applied.filters;
            const filtered = applied.filtered;
            const visible = this.offerableChoices(
              filtered.map((outdoor) => {
                const key = partnerKey(outdoor);
                let state = partnerChoiceState(outdoor);
                if (state.cartable) {
                  const ok = this.combosForOutdoor(key).some((combo) => comboCartable(combo, 'air_handler'));
                  if (!ok) state = { cartable: false, reason: 'path' };
                }
                return this.applyCartability({ key, outdoor }, state);
              })
            );
            this.renderPickerStep({
              items: visible,
              filters: this.outdoorFilters,
              groups: applied.groups,
              onSearch: (value) => {
                this.outdoorFilters.search = value;
              },
              onToggle: (tier, value) => {
                this.outdoorFilters = prunePartnerFilters(
                  applied.pool,
                  this.toggleFilterValue(this.outdoorFilters, tier, value),
                  AH_OUTDOOR_FILTER_TIERS
                );
              },
              searchPlaceholder: LABELS.outdoorSearchPh || 'EL22, ML13, SKU…',
              countText: (LABELS.outdoorCount || '{{ n }} outdoor units').replace(
                '{{ n }}',
                String(visible.length)
              ),
              emptyText:
                filtered.length && !visible.length
                  ? LABELS.pathUnavailable || 'No catalog products for this system type'
                  : LABELS.outdoorEmpty ||
                    'No outdoor units match these filters. Clear a chip or search to see more.',
              selectedKey: this.choice.outdoorKey,
              onPick: (item) => {
                if (item.cartable === false) return;
                this.choice.outdoorKey = item.key;
                this.choice.heatKitKey = '';
                this.choice.combo = null;
                this.heatKitFilters = this.blankPartnerFilters();
                this.resetFilterAccordion();
                this.step = stepAfterAirHandlerPartner(
                  this.heatKitOptions(item.key, 'outdoor'),
                  item.outdoor,
                  'air_handler'
                );
                if (this.step === 'summary') this.choice.combo = this.comboForAirHandler();
                this.render();
              },
              titleFn: (item) => item.outdoor.title || item.key,
              metaFn: (item) => outdoorMeta(item.outdoor),
            });
            return;
          }

          if (this.step === 'heat_kit') {
            this.renderHeatKitStep(this.choice.outdoorKey, 'outdoor');
            return;
          }

          this.choice.combo = this.choice.combo || this.comboForAirHandler();
          this.renderSummary(['Outdoor', 'Air Handler', 'Heat kit']);
        }

        renderHeatKitStep(filterKey, by) {
          this.stepLabel.textContent =
            this.pdpRole === 'air_handler'
              ? LABELS.stepHeatKit || 'Step 2 — Optional heat kit'
              : LABELS.stepHeatKitAfterType || 'Step 3 — Optional heat kit';
          const outdoor = this.chosenOutdoor();
          const airHandler = this.chosenAirHandler();
          const systemBtu = systemBtuFromSizeCode(outdoor, airHandler);
          const kits = this.heatKitOptions(filterKey, by);
          const skip = heatKitSkipChoice(LABELS);
          const pickKit = (item) => {
            if (item.cartable === false) return;
            this.choice.heatKitKey = item.key;
            this.choice.combo = this.comboForAirHandler();
            this.step = 'summary';
            this.render();
          };
          const note = document.createElement('p');
          note.className = 'ahri-package-furnace__note';
          note.textContent =
            LABELS.heatKitOptional || 'Optional — not required for AHRI certification.';
          this.body.appendChild(note);

          if (heatKitStepEmpty(kits)) {
            const wrap = document.createElement('div');
            wrap.className = 'ahri-package-furnace';
            wrap.appendChild(this.renderChoiceList([skip], this.choice.heatKitKey, pickKit, (item) => item.title));
            this.body.appendChild(wrap);
            return;
          }
          const applied = applyPartnerFilters(
            kits,
            this.heatKitFilters || this.blankPartnerFilters(),
            HEAT_KIT_FILTER_TIERS
          );
          this.heatKitFilters = applied.filters;
          const visible = applied.filtered;
          const visibleKits = this.offerableChoices(
            visible.map((kit) =>
              this.applyCartability(
                {
                  key: partnerKey(kit),
                  title: kit.title || partnerKey(kit),
                  kit,
                  recommended: heatKitIsRecommended(kit, systemBtu),
                },
                partnerChoiceState(kit)
              )
            )
          );
          this.renderPickerStep({
            items: visibleKits,
            filters: this.heatKitFilters,
            groups: applied.groups,
            onSearch: (value) => {
              this.heatKitFilters.search = value;
            },
            onToggle: (tier, value) => {
              this.heatKitFilters = prunePartnerFilters(
                applied.pool,
                this.toggleFilterValue(this.heatKitFilters, tier, value),
                HEAT_KIT_FILTER_TIERS
              );
            },
            searchPlaceholder: LABELS.kitSearchPh || 'ECB45, 10kW, SKU…',
            countText: (LABELS.kitCount || '{{ n }} heat kits').replace('{{ n }}', String(visibleKits.length)),
            emptyText: LABELS.kitEmpty || 'No heat kits match these filters. Clear a chip or search to see more.',
            selectedKey: this.choice.heatKitKey,
            prependItems: [skip],
            onPick: pickKit,
            titleFn: (item) => item.title,
            metaFn: (item) =>
              item.kit
                ? heatKitMeta(item.kit, {
                    recommended: item.recommended,
                    recommendedLabel: LABELS.kitRecommended || 'Recommended',
                  })
                : '',
          });
        }

        renderSummary(roles) {
          this.stepLabel.textContent = LABELS.stepSummary || 'Your package';
          const combo = this.choice.combo;
          if (!combo) {
            const note = document.createElement('p');
            note.className = 'ahri-package-dialog__status';
            note.textContent = airHandlerComboBlockedMessage(null, this.choice.heatKitKey, LABELS);
            this.body.appendChild(note);
            return;
          }
          const list = document.createElement('ul');
          list.className = 'ahri-package-dialog__summary';
          const map = {
            Outdoor: combo.outdoor,
            Furnace: combo.furnace,
            Coil: combo.coil,
            'Air Handler': combo.air_handler,
            'Heat kit': chosenHeatKitPart(this.choice, combo, this.catalogHeatKits),
          };
          roles.forEach((role) => {
            const part = map[role];
            if (!part) return;
            const li = document.createElement('li');
            li.textContent = `${role}: ${part.title || ''}`.trim();
            list.appendChild(li);
          });
          if (combo.ahri) {
            const li = document.createElement('li');
            li.textContent = ahriCertLabel(
              combo,
              this.pdpRole === 'coil'
                ? LABELS.ahriCertCoil || 'AHRI cert #{{ n }} — certified combo for this coil + furnace'
                : ''
            );
            list.appendChild(li);
          }
          this.body.appendChild(list);
          if (!this.canAddChosen()) {
            const note = document.createElement('p');
            note.className = 'ahri-package-dialog__status';
            note.textContent = LABELS.empty || 'No catalog products to add for this matchup.';
            this.body.appendChild(note);
          }
        }

        async addChosen() {
          const combo = this.choice.combo;
          const coilBlocked = coilRefused(combo && combo.coil);
          if (!combo || !this.canAddChosen() || coilBlocked) {
            if (this.status) {
              this.status.hidden = false;
              if (coilBlocked) {
                appendNoStockedCoil(this.status);
              } else {
                this.status.textContent = combo
                  ? LABELS.empty || 'No catalog products to add for this matchup.'
                  : airHandlerComboBlockedMessage(null, this.choice.heatKitKey, LABELS);
              }
            }
            return;
          }
          const onAhPath = this.pdpRole === 'air_handler' || this.choice.path === 'air_handler';
          const parts = onAhPath
            ? [combo.outdoor, combo.air_handler, chosenHeatKitPart(this.choice, combo, this.catalogHeatKits)]
            : [combo.outdoor, combo.furnace, combo.coil];
          const resolved = [];
          for (const part of parts) {
            if (!part) continue;
            resolved.push(await resolvePartVariant(part));
          }
          if (resolved.some((part) => part && part.coilUnmatched)) {
            if (this.status) {
              this.status.hidden = false;
              appendNoStockedCoil(this.status);
            }
            return;
          }
          const ids = variantIdsToAdd(resolved);
          this.addBtn.setAttribute('aria-disabled', 'true');
          try {
            const result = await addMissingVariantIds(ids);
            if (result.empty) {
              if (this.status) {
                this.status.hidden = false;
                this.status.textContent = statusMessage(result);
              }
              return;
            }
            this.close();
          } catch (error) {
            if (isSectionRefreshError(error)) {
              this.close();
              return;
            }
            if (this.status) {
              this.status.hidden = false;
              this.status.textContent = error.message;
            }
          } finally {
            this.addBtn.removeAttribute('aria-disabled');
          }
        }
      }
    );
  }

  function refreshPdpHeatKitRecommendations() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;
    document.querySelectorAll('ahri-compatible-systems').forEach((el) => {
      if (el && typeof el.decorateHeatKits === 'function') el.decorateHeatKits();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindAddSystemButtons(document);
    refreshPdpHeatKitRecommendations();
  });
  document.addEventListener('shopify:section:load', (event) => bindAddSystemButtons(event.target));
  if (document.readyState !== 'loading') {
    bindAddSystemButtons(document);
    refreshPdpHeatKitRecommendations();
  }
})();
